//#region Imports

import IntraoralViewer from "./IntraoralViewer.vue";
import IntraoralAddDialog from "./IntraoralAddDialog.vue";
import IntraoralEditNumberDialog from "./IntraoralEditNumberDialog.vue";
import IntraoralListPanel from "./IntraoralListPanel.vue";
import IntraoralListItem from "./IntraoralListItem.vue";
import swal from "sweetalert";

import {
  createGraph,
  getShortPath,
  getPointIndex,
  getVertices,
  getFaces,
} from "./GraphHelper";
import {
  drawTeethNumbers,
  removeTeethNumbers,
  showToothNumber,
} from "./IntraoralNumberMode";
import {
  getDistance,
  getDistance2D,
  getFacesGeometry,
  fillArea,
  areNearPoints,
  clearTempMeshes,
  removeBorderPointsFaces,
  drawSpherePoint,
  drawBox,
  drawLine,
  drawPoint,
} from "./IntraoralDraw";
import {
  createIoPoint,
  updateIoPointScale,
  createTubeCurve,
  updateTubeCurve,
  updateTubeLine,
  updateBezierPoints,
} from "./IntraoralTube";
import {
  getMergedGeometry,
  exportMeshToStlBlob,
  getGeometryFromStl,
  getGeometryFromDrc,
  getGeometryFromGlb,
} from "./IntraoralLoader";
import { getColorFromToothNumber } from "./TeethColors";
import {
  sendSaveIntraoralDataRequest,
  sendApplyBorderPointsRequest,
  sendStartPostprocessingRequest,
  checkPatientInPostprocess,
} from "@/api/intraoralApi";
import { degreesToRadians } from "@/components/geometry/geometry.js";
import {
  loadOrthoStage,
  downloadOrhoStages,
} from "@/components/ortho/OrthoPlanMode.js";

//#endregion

//#region Fields

const randomColorValue = "random";
const monochromeColorValue = "monochrome";
const greyColorValue = "#D0CECF";
const gingivaColor = "#ffb6c1";
let selectedTeethColor = "random";

const EXTR_TAG = "extr_";
const CUT_TAG = "cut_";
const SMOOTHED_TAG = "smoothed_";
const TOOTH_TAG = "tooth_";
const STL_TAG = ".stl";

const sceneBackgroundColor = "#082F5E"; //"#77a7fd";
const pongMaterial = new THREE.MeshPhongMaterial({
  // color: gingivaColor,
  flatShading: false,
  // flatShading: true,
  specular: 0x050505,
  shininess: 200,
  side: 2,
  polygonOffset: true,
  // transparent: true,
  // polygonOffsetUnit: 1,
  // wireframe: false,
});

const addedSuffix = "added_";
const upperSuffix = "upper_";
const lowerSuffix = "lower_";
const globalDz = 0;
const cameraDistance = 30;
const camera_dz = 0;

let viewerInitialized = false;
let controlsInitialized = false;
let allModels = [];

let isDoubleMode = false;
let isUpperMode = false;
let isEditMode = false;
let editDataLoaded = false;
// let editDataRequested = false;
let showNumbers = false;
let filledTeethLoaded = false;
let isChangingToothMouseUp = false;

let ioViewer;
let ioPlane = null;
let ioRaycaster = new THREE.Raycaster(
  new THREE.Vector3(),
  new THREE.Vector3(),
  0.0001,
  100000
);
let defaultIoBox = null;
let defaultIoZoom = null;

let selectedTooth = null;
let dblClickTooth = null;
let dblClickBorderGroup = null;
let taggedControl = null;

let wheelPoint = null;
let downPoint = null;
let dragPoint = null;
let intersectedTooth = null;
let mdTime = null;

let teeth = [];
let gingivae = [];
let teethMeshes = [];
let upperGingiva = null;
let lowerGingiva = null;

let upperMesh = null;
let upperBox = null;
let upperCameraCenter = null;
let upperGraph = null;
let lowerMesh = null;
let lowerBox = null;
let lowerCameraCenter = null;
let lowerGraph = null;
let upperCrossedPoints = [];
let lowerCrossedPoints = [];

let intraoralMenu = null;
let intraoralAddBtn = null;
let intraoralAddBorderBtn = null;
let intraoralDeleteBorderBtn = null;
let intraoralFocusBtn = null;
let intraoralDeleteBtn = null;
let intraoralChangeBtn = null;
let intraoralCenterBtn = null;
let intraoralFlipViewBtn = null;
let addToothGroup = null;
let addToothName = null;
let addToothNumber = null;

let ioChanges = [];
// let sampleChange = {
//   model: null,
//   oldGeometry: null,
//   oldPoints: null,
//   oldAnchorPoints: null,
//   oldAnchorGroup: null,
//   newGeometry: null,
//   newPoints: null,
//   newAnchorPoints: null,
//   newAnchorGroup: null,
// };
let currentIoChange = null;

let allItems = [];
let editedItem = { listItem: { newNumber: 7 } };
let viewMode = {
  isFilledTeethView: false,
  isEdit: isEditMode,
  isSaving: false,
  isPostprocessing: false,
  isUpper: true,
  isLower: false,
  isDouble: false,
  isLoaded: false,
  allowDownloads: true // vueApp.allowDownloads
};

//#endregion

//#region Init

function showIoViewer() {
  vueApp.leaveModes(true, false, false);
  vueApp.isIntraoralView = true;
  viewMode.allowDownloads = !vueApp.isViewMode && vueApp.allowDownloads;

  if (!viewerInitialized) {
    showLoader(false);
    const viewerInstance = new Vue({
      ...IntraoralViewer,
      propsData: {
        viewMode: viewMode,
      },
    });
    const vueContainer = document.createElement("div");
    document.getElementById("main_div").appendChild(vueContainer);
    viewerInstance.$mount(vueContainer);

    viewerInitialized = true;
  }

  initControls();

  if (vueApp.isViewMode) {
    isEditMode = false;
  }

  document.querySelector("#views_cont").style.display = "none";
  $(".menu3d").css({ display: "none" });
  // $(".menu3d").attr("disabled", true);

  document.querySelector("#intraoralViewer").style.display = "block";
  if (ioViewer) ioViewer.do_resize();
}

function setAllModels(models) {
  allModels = models;
}

function initStlViewer() {
  // console.log("initStlViewer", allModels);

  upperCrossedPoints = fillCrossedPoints(allModels.filter((m) => m.isUpper));
  lowerCrossedPoints = fillCrossedPoints(allModels.filter((m) => !m.isUpper));

  ioViewer = new StlViewer(document.getElementById("intraoralContainer"), {
    models: allModels,
    load_three_files: "js/",
    // center_models: false,
    controls: 1,
    // zoom: -1,
  });
  ioViewer.scene.background = new THREE.Color(sceneBackgroundColor);
  ioViewer.controls.zoomSpeed = 2.1;
  ioViewer.controls.noZoom = true;
  // ioViewer.controls.keysEnabled = false;

  setTimeout(async () => {
    await loadCutTeethFiles(allModels);
    stlAllLoaded();
  }, 100);
}

async function loadCutTeethFiles(stlModels) {
  let cutFilePromises = [];
  stlModels.forEach((x) => {
    let cutZipFile = x.cutZipFile;
    if (cutZipFile) {
      let elementName = x.name;
      let model = allModels.find((m) => m.name == elementName);
      let promise = cutZipFile.async("arraybuffer");
      cutFilePromises.push(promise);
      promise.then((buffer) => {
        let geometry = getGeometryFromStl(buffer);
        const tempGeo = new THREE.Geometry().fromBufferGeometry(geometry);
        tempGeo.mergeVertices();
        tempGeo.computeVertexNormals();
        geometry = new THREE.BufferGeometry().fromGeometry(tempGeo);

        model.filledMesh = new THREE.Mesh(geometry, model.mesh.material);
        model.filledMesh.visible = false;
        ioViewer.scene.add(model.filledMesh);
      });
    }
  });
  if (cutFilePromises.length > 0) {
    await Promise.all(cutFilePromises);
    filledTeethLoaded = true;
    console.log("filled teeth loaded", cutFilePromises.length);
  }
  // .then((values) => {
  //     filledTeethLoaded = true;
  //   });
}

function fillCrossedPoints(models) {
  if (!models || !models.length) return;

  // console.log("fillCrossedPoints start", new Date());

  let crossedPoints = [];
  return crossedPoints;
  let allVolumes = models
    .filter((m) => m.isTooth)
    .map((model) => {
      let mesh = model.mesh;
      if (!mesh.geometry.boundingBox) {
        mesh.geometry.computeBoundingBox();
      }
      let box = mesh.geometry.boundingBox;
      // box = offsetBox(box, 0, 0, 0);
      let vertices = getVertices(mesh);
      let volume = {
        box: box,
        model: model,
        vertices: vertices,
      };
      model.crossedPoints = [];
      model.crossedTeeth = [];
      return volume;
    });

  // allVolumes.forEach((volume) => {
  //   let distance = Infinity;
  //   for (let vl of allVolumes) {
  //     let dst = getDistance(volume.center, vl.center);
  //   }
  //   console.log(volume.model.name, volume);
  // });

  allVolumes.forEach((vl) => {
    allVolumes.forEach((nvl) => {
      if (nvl != vl && nvl.box.intersectsBox(vl.box)) {
        vl.vertices.forEach((v) => {
          nvl.vertices.forEach((nv) => {
            if (v.x == nv.x && v.y == nv.y && v.z == nv.z) {
              crossedPoints.push(v);
              vl.model.crossedPoints.push(v);
              // console.log(vl.model.name, nvl.model.name, v);
              // drawPoint(v, ioViewer.scene);
            }
          });
        });
        vl.model.crossedTeeth.push(nvl.model.name);
      }
    });
  });
  // crossedPoints = [...new Set(crossedPoints)];
  // console.log("fillCrossedPoints end", crossedPoints.length, new Date());
  return crossedPoints;
}

function applyBorderPoints(borderPoints, isBasePoints) {
  for (let prop in borderPoints) {
    let name = prop.replace("stl", "").replace(".", "");
    let model = getModelByName(name);
    if (model) {
      if (isBasePoints) {
        model.basePoints = model.points = borderPoints[prop];
      } else {
        model.savedPoints = model.points = borderPoints[prop];
      }
    }
  }
}

function applyAnchorPoints(anchorPoints, isBasePoints, isUpper) {
  // console.log("applyAnchorPoints", isBasePoints, anchorPoints);
  if (!anchorPoints) return;

  let anchorKeys = Object.keys(anchorPoints);
  if (anchorKeys.length == 0) return;

  for (let model of allModels) {
    let modelName = model.name;
    let modelPoints = anchorPoints[modelName];
    if (modelPoints) {
      let anchorGroup = [modelPoints];
      let i = 1;
      let borderName = `${modelName}-${i}`;
      while (anchorPoints[borderName]) {
        anchorGroup.push(anchorPoints[borderName]);
        i++;
        borderName = `${modelName}-${i}`;
      }

      if (anchorGroup.length > 1) {
        anchorGroup.sort((arr1, arr2) => arr2.length - arr1.length);
        // console.log(modelName, anchorGroup);
        modelPoints = anchorGroup[0];
      }

      if (isBasePoints) {
        model.baseAnchorPoints = model.anchorPoints = modelPoints;
        model.baseAnchorGroup = model.anchorGroup = anchorGroup;
      } else {
        model.savedAnchorPoints = model.anchorPoints = modelPoints;
        model.savedAnchorGroup = model.anchorGroup = anchorGroup;
        if (!model.baseAnchorPoints) {
          model.baseAnchorPoints = model.savedAnchorPoints;
          model.baseAnchorGroup = model.savedAnchorGroup;
        }
      }
    } else if (
      model.isTooth &&
      model.isUpper == isUpper &&
      !isBasePoints &&
      !model.isDeleted &&
      (model.isAdded || model.baseAnchorPoints)
    ) {
      console.log("DELETED in applyAnchorPoints", model);
      model.isDeleted = true;
    }
  }
}

function stlAllLoaded() {
  console.log("intraoral stlAllLoaded");
  console.time("intraoral stlAllLoaded");

  $id("loader").style.display = "none";
  console.log(`stlAllLoaded allModels`, allModels.length);
  ioPlane = new THREE.Mesh(
    new THREE.PlaneBufferGeometry(500, 500, 8, 8),
    new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      opacity: 0.01,
    })
  );
  ioViewer.scene.add(ioPlane);
  ioViewer.controls.screenSpacePanning = true;
  ioViewer.controls.addEventListener("change", onCameraChange);
  ioViewer.renderer.outputEncoding = THREE.sRGBEncoding;
  // ioViewer.scene.add(new THREE.AxesHelper(80));

  let currentMin = { x: ioViewer.minx, y: ioViewer.miny, z: ioViewer.minz };
  let currentMax = { x: ioViewer.maxx, y: ioViewer.maxy, z: ioViewer.maxz };
  let currentBox = { min: currentMin, max: currentMax };
  defaultIoBox = convertToBox(currentBox);
  console.log("defaultIoBox", defaultIoBox);

  upperGingiva = allModels.find((x) => x.isGingiva && x.isUpper);
  lowerGingiva = allModels.find((x) => x.isGingiva && !x.isUpper);
  let upperMeshes = [];
  let lowerMeshes = [];

  allModels.forEach((model) => {
    let vmesh = model.mesh;
    vmesh.name = model.name;
    vmesh.material.flatShading = false;
    vmesh.material.color = new THREE.Color(model.color);
    model.uuid = vmesh.uuid;

    if (!model.isGingiva) {
      teeth.push(model);
      teethMeshes.push(vmesh);
    } else {
      gingivae.push(model);
      if (model.isUpper) upperGingiva = model;
      else lowerGingiva = model;
    }
    if (model.isUpper) upperMeshes.push(vmesh);
    else lowerMeshes.push(vmesh);

    let box = vmesh.geometry.boundingBox;
    box = offsetBox(box, 0, 0, 0);
    model.defaultBox = box;
    // vmesh.position.set(box.center.x, box.center.y, box.center.z);

    let geometry = prepareGeometry(vmesh.geometry, model.isGingiva);
    vmesh.geometry.dispose();
    vmesh.geometry = geometry;

    if (model.isGingiva && !vmesh.stages) {
      vmesh.stages = {};
      vmesh.stages[-1] = { geometry: vmesh.geometry.clone() };
    }
  });

  if (upperMeshes.length && upperGingiva) {
    upperMesh = createGlobalMesh(upperMeshes, upperGingiva);
    upperMesh.geometry.computeBoundingBox();
    upperBox = offsetBox(upperMesh.geometry.boundingBox);
    upperCameraCenter = new THREE.Vector3(
      upperBox.center.x,
      upperBox.center.y,
      upperBox.center.z
    );
    // upperGraph = createGraph(upperMesh);
  }
  if (lowerMeshes.length && lowerGingiva) {
    lowerMesh = createGlobalMesh(lowerMeshes, lowerGingiva);
    lowerMesh.geometry.computeBoundingBox();
    lowerBox = offsetBox(lowerMesh.geometry.boundingBox);
    lowerCameraCenter = new THREE.Vector3(
      lowerBox.center.x,
      lowerBox.center.y,
      lowerBox.center.z
    );

    // lowerGraph = createGraph(lowerMesh);
  }

  allModels.forEach((model) => {
    model.filename = `${model.name}.stl`;
    if (model.isGingiva) {
      return;
    }

    let vmesh = model.mesh;
    if (model.savedGeometry) {
      vmesh.geometry.dispose();
      vmesh.geometry = model.savedGeometry.clone();
      vmesh.geometry.computeBoundingBox();

      let box = vmesh.geometry.boundingBox;
      box = offsetBox(box, 0, 0, 0);
      model.defaultBox = box;
      model.defaultPosition = {
        x: 0, // box.center.x,
        y: 0, // box.center.y,
        z: 0, // box.center.z,
      };
      // vmesh.position.set(box.center.x, box.center.y, box.center.z);

      let geometry = prepareGeometry(vmesh.geometry, model.isGingiva);
      vmesh.geometry.dispose();
      vmesh.geometry = geometry;
    }
    model.savedGeometry = vmesh.geometry; //.clone();
  });

  let padding = -10;
  let w = (defaultIoBox.size.x + padding) / 2;
  let h = (defaultIoBox.size.y + padding) / 2;
  let fovX = ioViewer.camera.fov * ioViewer.camera.aspect;
  let fovY = ioViewer.camera.fov;
  let distanceX = w / Math.tan((Math.PI * fovX) / 360) + w;
  let distanceY = h / Math.tan((Math.PI * fovY) / 360) + w;
  defaultIoZoom = Math.max(distanceX, distanceY);

  allModels.forEach((model) => {
    // ioViewer.set_opacity(model.id, 0.7);
    ioViewer.set_opacity(model.id, 1);
    // ioViewer.set_opacity(model.id, model.isTooth ? 0.2 : 1);
    model.mesh.visible = !model.isDeleted;
  });

  if (upperMeshes.length > 0) {
    switchViewModes(true, false);
  } else {
    switchViewModes(false, true);
  }

  // upperCrossedPoints.forEach(p => {
  //   drawPoint(p, ioViewer.scene);
  // });

  attachEvents();

  startPostprocessWatch();
  $("#orthoMenu").removeAttr("style");
  switchEditMode(false);

  fillIntraoralList();
  $(".intraoral-list-ctrl").css("display", "block");
  $(`#intraoralListPanel`).addClass("show");
  viewMode.isLoaded = true;

  setTimeout(function () {
    loadOrtho();
  }, 100);
  // window.requestIdleCallback(requestLoadEditData);

  console.timeEnd("intraoral stlAllLoaded");
}

function prepareGeometry(baseGeometry, isGingiva) {
  // baseGeometry.center();
  // baseGeometry.computeVertexNormals();

  let geometry = new THREE.Geometry().fromBufferGeometry(baseGeometry);
  if (!isGingiva) {
    geometry.mergeVertices();
  }
  geometry.computeVertexNormals();

  geometry = new THREE.BufferGeometry().fromGeometry(geometry);
  geometry.computeBoundingBox();
  return geometry;
}

function createGlobalMesh(meshes, gingivaModel) {
  ioViewer.set_opacity(gingivaModel.id, 1);
  gingivaModel.mainMesh.visible = false;

  if (!gingivaModel.filledMesh) {
    let geometry = getMergedGeometry(meshes);
    let material = pongMaterial.clone();
    material.color = new THREE.Color(gingivaColor);
    material.polygonOffsetUnit = 1;
    material.polygonOffsetFactor = 9;

    let mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.name = gingivaModel.name;
    gingivaModel.filledMesh = mesh;
  }
  ioViewer.scene.add(gingivaModel.filledMesh);
  gingivaModel.mesh = gingivaModel.filledMesh;

  return gingivaModel.filledMesh;
}

function initControls() {
  if (!controlsInitialized) {
    var ctrls = [
      IntraoralAddDialog,
      IntraoralEditNumberDialog,
      IntraoralListPanel,
    ];
    ctrls.forEach((component) => {
      const componentInstance = new Vue({
        ...component,
        propsData: { item: editedItem, allItems: allItems },
      });
      const vueContainer = document.createElement("div");
      document.getElementById("intraoralViewer").appendChild(vueContainer);
      componentInstance.$mount(vueContainer);
    });
    controlsInitialized = true;
  }
}

function loadOrtho() {
  // if (vueApp.orthoStages.length < 1) {
  vueApp.downloadingStages = true;
  $("#orthoMenu").css({ display: "none" });
  downloadOrhoStages((stages) => {
    vueApp.downloadingStages = false;
    $("#orthoMenu").removeAttr("style");
    if (stages && stages.length) {
      let sorted = stages.sort((a, b) => a.id - b.id);
      sorted.forEach((stage) => {
        vueApp.orthoStages.push(stage);
      });
    }
  }, true);
  // }
}

function loadEditData() {
  if (!editDataLoaded) {
    editDataLoaded = true;
    showLoader(true);

    window.requestIdleCallback(requestLoadEditData);
  }
}

function requestLoadEditData(deadline) {
  console.log("requestLoadEditData", deadline);
  if (upperMesh) upperGraph = createGraph(upperMesh);
  if (lowerMesh) lowerGraph = createGraph(lowerMesh);
  // upperCrossedPoints = fillCrossedPoints(allModels.filter((m) => m.isUpper));
  // lowerCrossedPoints = fillCrossedPoints(allModels.filter((m) => !m.isUpper));
  editDataLoaded = true;
  hideLoader();

  if (!isEditMode) {
    isEditMode = viewMode.isEdit = true;
  }
}

//#endregion

//#region Item/Model Actions

function fillIntraoralList() {
  // console.log("fillIntraoralList ");

  const intraoralList = document.getElementById("intraoralList");
  intraoralList.innerHTML = "";
  allModels
    .filter((x) => !x.isTooth)
    .forEach((model) => {
      addItemInList(model);
    });

  allModels
    .filter((x) => x.isTooth)
    .forEach((model) => {
      addItemInList(model);
    });
}

function addItemInList(model) {
  const intraoralList = document.getElementById("intraoralList");
  let item = {
    id: model.id,
    uuid: model.uuid,
    name: model.name,
    number: model.number,
    newNumber: model.number,
    displayName: model.name,
    color: model.color,
    isTooth: model.isTooth,
    isVisible: true,
    isHovered: false,
    isClicked: false,
    isFocused: false,
    isDeleted: model.isDeleted,
    isUpper: model.isUpper,
    defaultPosition: model.defaultPosition,
    defaultBox: model.defaultBox,
  };
  item.filename = `${model.name}.stl`;
  model.listItem = item;
  allItems.push(item);

  const itemInstance = new Vue({
    ...IntraoralListItem,
    propsData: {
      item: item,
    },
  });
  const vueContainer = document.createElement("div");
  intraoralList.appendChild(vueContainer);
  itemInstance.$mount(vueContainer);
}

function changeItemVisibility(item) {
  if (!item) return;

  let model = getModelByName(item.name);
  if (model) {
    model.isVisible = item.isVisible;
  } else {
    return;
  }

  if (model.isGingiva && model.isVisible && viewMode.isFilledTeethView) {
    viewMode.isFilledTeethView = false;
    switchFilledTeethView(viewMode.isFilledTeethView);
  }

  if (item.isUpper == isUpperMode) {
    if (!item.isVisible && item.isFocused) {
      item.isFocused = false;
      switchItemFocus(item);
    }

    ioViewer.set_color(item.id, item.isVisible ? item.color : "transparent");

    if (model.mainMesh && model.mainMesh.numberGroup) {
      model.mainMesh.numberGroup.visible = item.isVisible;
    }

    if (model.pointsGroup && model.id == selectedTooth.id) {
      model.pointsGroup.visible =
        !item.isFocused && item.isVisible && isEditMode;
    }
  }
  changeTeethColor(selectedTeethColor);
}

function deleteItem(item) {
  if (item) {
    let model = getModelByName(item.name, true);
    deleteModel(model);
  }
}

function deleteModel(model) {
  if (model && checkIsEditMode()) {
    swal({
      text: "Are you sure you want to delete this tooth?",
      buttons: {
        yes: { text: "Yes", value: true },
        no: { text: "No", value: false },
      },
    }).then((willDelete) => {
      if (willDelete) {
        document.getElementById("selectedToothHint").innerHTML = "";

        if (model.listItem.isFocused) {
          model.listItem.isFocused = false;
          switchItemFocus(model.listItem);
        }

        model.isDeleted = true;
        model.listItem.isDeleted = true;
        let index = allItems.indexOf(model.listItem);
        allItems.splice(index, 1);

        model.recentlyDeleted = true;
        model.mesh.visible = false;
        if (model.pointsGroup) {
          ioViewer.scene.remove(model.pointsGroup);
        }

        let mesh = model.mainMesh;
        if (mesh.numberGroup) {
          ioViewer.scene.remove(mesh.numberGroup);
          mesh.numberGroup = null;
          mesh.numberMesh = null;
        }
        model.pointsGroup = null;
        // ioViewer.scene.remove(model.mesh);
        // let index = teeth.indexOf(selectedTooth);
        // teeth.splice(index, 1);
        // teethMeshes.splice(index, 1);
        selectedTooth = null;
      }
    });
  }
}

function editItemNumber(item) {
  if (item && checkIsEditMode()) {
    if (viewMode.isSaving || viewMode.isPostprocessing) {
      swal({
        text: "Cannot edit tooth settings while post processing.",
      });
      return;
    }

    item.newNumber = item.number;
    editedItem.listItem = item;
    $("#intraoralEditNumberDialog").modal();
  }
}

function changeToothNumber(item) {
  let oldNumber = item.number;
  let newNumber = item.newNumber;
  // let oldToothName = `tooth_${oldNumber}`;
  let newToothName = `tooth_${newNumber}`;
  let tooth = getModelByItem(item);
  if (tooth && !tooth.isDeleted) {
    tooth.name = item.name = item.displayName = newToothName;
    tooth.number = item.number = newNumber;
    document.getElementById("selectedToothHint").innerHTML = tooth.name;

    var mesh = tooth.mainMesh;
    if (mesh && mesh.numberGroup) {
      ioViewer.scene.remove(mesh.numberGroup);
      mesh.numberGroup = null;
      if (showNumbers) showToothNumber(tooth);
    }
  }
}

function highlightItem(item) {
  if (
    item &&
    item.isTooth &&
    item.isVisible &&
    !item.isHidden &&
    !item.isFocused &&
    item.isUpper === isUpperMode
  ) {
    var mesh = ioViewer.get_model_mesh(item.id);
    if (mesh) {
      var rgb = mesh.material.color;
      if (rgb.r == 1 && rgb.g == 1 && rgb.b == 1) {
        mesh.material.color.setRGB(0.8, 0.8, 0.8);
      } else {
        var r = Math.min(1.0, rgb.r + 0.2);
        var g = Math.min(1.0, rgb.g + 0.2);
        var b = Math.min(1.0, rgb.b + 0.2);
        mesh.material.color.setRGB(r, g, b);
      }
    }
  }
}

function unhighlightItem(item) {
  if (
    item &&
    item.isTooth &&
    item.isVisible &&
    !item.isHidden &&
    !item.isFocused &&
    item.isUpper === isUpperMode
  ) {
    let color =
      selectedTeethColor == randomColorValue ? item.color : selectedTeethColor;
    if (selectedTeethColor == monochromeColorValue) {
      color = greyColorValue;
    }
    ioViewer.set_color(item.id, color);
  }
}

function switchItemFocus(item) {
  let model = getModelByItem(item);
  if (model) model.isFocused = item.isFocused;

  if (item && item.isFocused) {
    item.isHidden = false;
    item.isVisible = true;
    if (model.isUpper != isUpperMode) {
      isUpperMode = !isUpperMode;
      viewMode.isUpper = isUpperMode;
      viewMode.isLower = !isUpperMode;
      switchViewModes(viewMode.isUpper, viewMode.isLower);
    }

    model.mesh.visible = model.isVisible =
      model.isUpper == isUpperMode && item.isVisible;

    if (model.pointsGroup) {
      //  && model.mesh.visible && model == selectedTooth
      model.pointsGroup.visible = false;
    }

    if (model.isGingiva) {
      resetIoCamera();
      if (isUpperMode) rotateIntraoralToView(2);
      model.mainMesh.visible = true;
      model.filledMesh.visible = false;
      model.mesh = model.mainMesh;
    } else {
      centerViewClick(model);
    }
  } else {
    resetIoCamera();
    if (isUpperMode) rotateIntraoralToView(2);
    selectedTooth = null;
  }

  allItems.forEach((x) => {
    if (x.isDeleted) return;
    if (x != item) {
      x.isFocused = false;
      x.isHidden = !!item && item.isFocused;
    }
    let xmodel = getModelByItem(x);
    if (xmodel.pointsGroup) {
      xmodel.pointsGroup.visible = false;
    }
    if (xmodel.mainMesh && xmodel.mainMesh.numberGroup) {
      xmodel.mainMesh.numberGroup.visible = false;
    }
    if (xmodel.isGingiva && !x.isFocused) {
      xmodel.mainMesh.visible = false;
      xmodel.filledMesh.visible = true;
      xmodel.mesh = xmodel.filledMesh;
    }
    xmodel.mesh.visible =
      xmodel.isUpper == isUpperMode && !x.isHidden && x.isVisible;
  });
}

//#endregion

//#region Events

function attachEvents() {
  intraoralMenu = document.getElementById("intraoral-tooth-menu");
  document.getElementById("intraoralViewer").appendChild(intraoralMenu);

  intraoralAddBtn = document.getElementById("intraoral-add-btn");
  intraoralAddBtn.addEventListener("click", () => {
    addClick();
  });

  intraoralAddBorderBtn = document.getElementById("intraoral-add-border-btn");
  intraoralAddBorderBtn.addEventListener("click", () => {
    addBorderClick();
  });

  intraoralDeleteBorderBtn = document.getElementById(
    "intraoral-delete-border-btn"
  );
  intraoralDeleteBorderBtn.addEventListener("click", () => {
    deleteBorderClick();
  });

  intraoralFocusBtn = document.getElementById("intraoral-focus-btn");
  intraoralFocusBtn.addEventListener("click", () => {
    focusClick();
  });

  intraoralDeleteBtn = document.getElementById("intraoral-delete-btn");
  intraoralDeleteBtn.addEventListener("click", () => {
    deleteClick();
  });

  intraoralChangeBtn = document.getElementById("intraoral-change-btn");
  intraoralChangeBtn.addEventListener("click", () => {
    changeClick();
  });

  intraoralCenterBtn = document.getElementById("intraoral-center-btn");
  intraoralCenterBtn.addEventListener("click", () => {
    centerViewClick();
  });

  intraoralFlipViewBtn = document.getElementById("intraoral-flip-btn");
  intraoralFlipViewBtn.addEventListener("click", () => {
    flipViewClick();
  });

  let intraoralContainer = document.getElementById("intraoralContainer");
  intraoralContainer = [...intraoralContainer.children].find(
    (c) => c.tagName.toLowerCase() == "canvas"
  );
  let is_touch_enabled =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0;
  if (is_touch_enabled) {
    intraoralContainer.addEventListener("touchstart", function (e) {
      var event = e.targetTouches[0];
      onDocumentMouseDown(event);
    });
    intraoralContainer.addEventListener("touchmove", function (e) {
      var event = e.targetTouches[0];
      onDocumentMouseMove(event);
    });
    intraoralContainer.addEventListener("touchend", function (e) {
      var event = e.changedTouches[0];
      onDocumentMouseUp(event);
    });
  } else {
    intraoralContainer.addEventListener(
      "mousedown",
      onDocumentMouseDown,
      false
    );
    intraoralContainer.addEventListener(
      "mousemove",
      onDocumentMouseMove,
      false
    );
    intraoralContainer.addEventListener("mouseup", onDocumentMouseUp, false);
    intraoralContainer.addEventListener("dblclick", onDocumentDblClick, false);
    intraoralContainer.addEventListener("wheel", onDocumentWheel, false);
    intraoralContainer.oncontextmenu = function (e) {
      stopEvent(e, true);
    };

    document.onkeydown = function (e) {
      if (vueApp.isIntraoralView && e.ctrlKey) {
        stopEvent(e, true);
        if (e.key == "z") {
          intraoralUndo();
        }
        if (e.key == "y") {
          intraoralRedo();
        }
      }
    };
  }
}

function addClick() {
  intraoralMenu.style.display = "none";
  if (checkIsEditMode()) {
    $("#intraoralAddDialog").modal();
  }
}

function addBorderClick() {
  intraoralMenu.style.display = "none";
  console.log("addBorderClick");
  if (checkIsEditMode() && selectedTooth) {
    addToothGroup = new THREE.Group();
    selectedTooth.pointsGroup.add(addToothGroup);
  }
}

function deleteBorderClick() {
  intraoralMenu.style.display = "none";
  if (checkIsEditMode() && selectedTooth && dblClickBorderGroup) {
    let children = selectedTooth.pointsGroup.children;
    let length = children.length;
    let groupIndex = children.indexOf(dblClickBorderGroup);
    console.log("deleteBorderClick", groupIndex);
    if (length == 1) {
      swal({
        text: "It's the only tooth border. Do you want to delete this tooth?",
        buttons: {
          yes: { text: "Yes", value: true },
          no: { text: "No", value: false },
        },
      }).then(async (willDelete) => {
        if (willDelete) {
          deleteModel(selectedTooth);
        }
      });
    } else {
      swal({
        text: "Are you sure you want to delete this border?",
        buttons: {
          yes: { text: "Yes", value: true },
          no: { text: "No", value: false },
        },
      }).then(async (willDelete) => {
        if (willDelete) {
          selectedTooth.pointsGroup.remove(dblClickBorderGroup);
          selectedTooth.isChanged = true;
        }
      });
    }
  }
}

function cancelAdd() {
  $(".intraoral-add-item").css("display", "none");
  if (addToothGroup) {
    ioViewer.scene.remove(addToothGroup);
    addToothGroup = null;
  }
}

function focusClick() {
  intraoralMenu.style.display = "none";
  if (dblClickTooth && dblClickTooth.listItem) {
    dblClickTooth.isFocused = dblClickTooth.listItem.isFocused =
      !dblClickTooth.listItem.isFocused;

    switchItemFocus(dblClickTooth.listItem);
  }
}

function deleteClick() {
  intraoralMenu.style.display = "none";
  if (selectedTooth) {
    deleteModel(selectedTooth);
  }
}

function changeClick() {
  intraoralMenu.style.display = "none";
  if (selectedTooth) {
    editItemNumber(selectedTooth.listItem);
  }
}

function centerViewClick(tooth) {
  intraoralMenu.style.display = "none";
  if (!tooth) {
    tooth = dblClickTooth;
  }
  if (!tooth) {
    tooth = selectedTooth;
  }
  if (tooth) {
    tooth.isCenterView = true;
    tooth.isBacksideView = false;

    setToothCameraPoints(tooth);

    if (tooth.centerCameraPoint) {
      let box = tooth.defaultBox;

      rotateIntraoralToView(3);

      ioViewer.camera.position.copy(tooth.centerCameraPoint);

      ioViewer.controls.target.copy(
        new THREE.Vector3(box.center.x, box.center.y, box.center.z)
      );
    }
  }
}

function flipViewClick(tooth) {
  intraoralMenu.style.display = "none";
  if (!tooth) {
    tooth = dblClickTooth;
  }
  if (!tooth) {
    tooth = selectedTooth;
  }

  if (tooth) {
    if (tooth.isBacksideView) {
      centerViewClick();
      return;
    }

    tooth.isBacksideView = true;
    tooth.isCenterView = false;
    setToothCameraPoints(tooth);

    if (tooth.backsideCameraPoint) {
      rotateIntraoralToView(3);

      ioViewer.camera.position.copy(tooth.backsideCameraPoint);

      let box = tooth.defaultBox;
      ioViewer.controls.target.copy(
        new THREE.Vector3(box.center.x, box.center.y, box.center.z)
      );
    }
  }
}

function setToothCameraPoints(tooth) {
  if (tooth && !tooth.centerCameraPoint) {
    let mesh = ioViewer.get_model_mesh(tooth.id);
    if (mesh) {
      // clearTempMeshes(ioViewer.scene);
      if (!tooth.defaultBox) {
        mesh.geometry.computeBoundingBox();
        tooth.defaultBox = offsetBox(mesh.geometry.boundingBox);
      }

      let tootBoxCenter = tooth.defaultBox.center;
      let globalCenter = tooth.isUpper ? upperCameraCenter : lowerCameraCenter;

      let centersDistance = calculateDistance(tootBoxCenter, globalCenter);
      if (!tooth.centerCameraPoint) {
        let coef = (centersDistance + cameraDistance) / centersDistance;

        let px = (tootBoxCenter.x - globalCenter.x) * coef + globalCenter.x;
        let py = (tootBoxCenter.y - globalCenter.y) * coef + globalCenter.y;
        let pz = tootBoxCenter.z; // (tootBoxCenter.z - center.z) * coef + globalCenter.z;
        tooth.centerCameraPoint = new THREE.Vector3(px, py, pz);
        // drawSpherePoint(ioViewer.scene, tooth.centerCameraPoint, 0xff0000, 1);
        // drawLine(tootBoxCenter, tooth.centerCameraPoint,  null, ioViewer.scene);
      }

      if (!tooth.backsideCameraPoint) {
        let coef = (centersDistance - cameraDistance) / centersDistance;
        let px = (tootBoxCenter.x - globalCenter.x) * coef + globalCenter.x;
        let py = (tootBoxCenter.y - globalCenter.y) * coef + globalCenter.y;
        let pz = tootBoxCenter.z + tooth.defaultBox.size.z;
        tooth.backsideCameraPoint = new THREE.Vector3(px, py, pz);
      }
    }
  }
}

function onDocumentWheel(event) {
  updateRaycasterFromEvent(event);
  wheelPoint = intersectMesh(getGlobalMesh());

  var factor = 5;

  let intraoralContainer = document.getElementById("intraoralContainer");
  let overlayRect = intraoralContainer.getBoundingClientRect();

  var mX = (event.clientX / overlayRect.width) * 2 - 1;
  var mY = -(event.clientY / overlayRect.height) * 2 + 1;
  var vector = new THREE.Vector3(mX, mY, 0.1);

  let trackBallControls = ioViewer.controls;
  // drawPoint(trackBallControls.target, ioViewer.scene, 0x00ff00, 2);
  let camera = ioViewer.camera;

  vector.unproject(camera);
  vector.sub(camera.position);
  if (event.deltaY < 0) {
    camera.position.addVectors(camera.position, vector.setLength(factor));
    trackBallControls.target.addVectors(
      trackBallControls.target,
      vector.setLength(factor)
    );
  } else {
    camera.position.subVectors(camera.position, vector.setLength(factor));
    trackBallControls.target.subVectors(
      trackBallControls.target,
      vector.setLength(factor)
    );
  }
  // drawPoint(trackBallControls.target, ioViewer.scene, 0xff0000, 2);
  if (selectedTooth) {
    // console.log("onDocumentWheel toothCameraDistance", toothCameraDistance);
    let group = selectedTooth.pointsGroup;
    if (group && group.visible) {
      updatePointsGroupVisibility(selectedTooth, true);
    }
  }
}

function onDocumentDblClick(event) {
  updateRaycasterFromEvent(event);
  let isFocused = isAnyToothFocused();

  let dblClickPointsGroupMesh = null;
  if (selectedTooth && selectedTooth.pointsGroup) {
    dblClickPointsGroupMesh = intersectPointsGroup(selectedTooth.pointsGroup);
  }
  dblClickBorderGroup = dblClickPointsGroupMesh
    ? dblClickPointsGroupMesh.parent
    : null;

  dblClickTooth = dblClickPointsGroupMesh ? selectedTooth : intersectTooth();
  console.log("onDocumentDblClick", dblClickBorderGroup, dblClickTooth);

  intraoralFocusBtn.innerHTML =
    dblClickTooth && isFocused ? "Unfocus Tooth" : "Focus on Tooth";

  // intraoralAddBtn.style.display = !dblClickTooth ? "initial" : "none";

  intraoralDeleteBorderBtn.style.display =
    dblClickPointsGroupMesh && !isFocused ? "initial" : "none";

  intraoralAddBorderBtn.style.display =
    dblClickTooth && !isFocused ? "initial" : "none";

  intraoralFocusBtn.style.display =
    intraoralDeleteBtn.style.display =
    intraoralChangeBtn.style.display =
    intraoralCenterBtn.style.display =
    intraoralFlipViewBtn.style.display =
      dblClickTooth ? "initial" : "none";

  if (!isEditMode) {
    intraoralDeleteBtn.style.display =
      intraoralChangeBtn.style.display =
      intraoralDeleteBorderBtn.style.display =
      intraoralAddBorderBtn.style.display =
        "none";
  }

  intraoralAddBtn.style.display = isEditMode && !isFocused ? "initial" : "none";

  let firstVisibleNode = [...intraoralMenu.childNodes].find(
    (x) => x.style.display != "none"
  );
  if (firstVisibleNode && firstVisibleNode != intraoralAddBtn) {
    firstVisibleNode.style.borderTop = "0";
  } else {
    [...intraoralMenu.childNodes]
      .filter((x) => x != intraoralAddBtn && x.style.display != "none")
      .forEach((x) => {
        x.style.borderTop = "1px solid #D9E6FE";
      });
  }

  intraoralMenu.style.display = "initial";
  intraoralMenu.style.left = event.offsetX + 1 + "px";
  intraoralMenu.style.top = event.offsetY + 1 + "px";

  // updateRaycaster(event);
  // let point = intersectScene();
  // if (point) {
  //   let canvasHalfWidth = ioViewer.renderer.domElement.offsetWidth / 2;
  //   let canvasHalfHeight = ioViewer.renderer.domElement.offsetHeight / 2;

  //   let canvasPoint = point.project(ioViewer.camera);
  //   canvasPoint.x = canvasPoint.x * canvasHalfWidth + canvasHalfWidth;
  //   canvasPoint.y = -(canvasPoint.y * canvasHalfHeight) + canvasHalfHeight;
  // }
}

function onDocumentMouseDown(event) {
  stopEvent(event);
  downPoint = { x: event.clientX, y: event.clientY };

  intraoralMenu.style.display = "none";

  mdTime = new Date();

  console.log("onDocumentMouseDown");
  if (
    vueApp.activeMode &&
    vueApp.activeMode.isActive &&
    vueApp.activeMode.tagDetected
  ) {
    if (event.button && event.button > 0) {
      return;
    }

    updateRaycasterFromEvent(event);
    var selectedId = vueApp.activeMode.selectedId;
    if (selectedId) {
      const isShift = event.shiftKey;
      // console.log("isShift", isShift);
      var tagged = findTaggedControl("group", false, isShift);
      if (tagged) return true;
    }
  }

  if (!isEditMode && !addToothGroup) return;

  updateRaycasterFromEvent(event);

  if (selectedTooth && !addToothGroup) {
    let point = intersectPoint(selectedTooth.pointsGroup);
    if (point) {
      allItems.forEach((x) => {
        x.isHovered = false;
      });
      startDragPoint(point, event);
      return;
    }
  }
  if (event.button === 0) {
    intersectedTooth = intersectTooth();
  }
}

function findTaggedControl(tagParam = "group", isHighlight, isShift) {
  // need precall -  updateRaycaster(event);
  var combinedArray = [];
  ioViewer.scene.children.forEach((element) => {
    if (element.tag == tagParam) {
      combinedArray.push(...element.children);
    }
  });
  var intersects = ioRaycaster.intersectObjects(combinedArray);
  var intersectPoint = null;
  var tagged = intersects.find((intersect) => {
    intersectPoint = intersect.point;
    return intersect.object.tag != undefined;
  });

  if (taggedControl != tagged) {
    combinedArray.forEach((child) => {
      if (child.defaultColor) {
        child.material.color = new THREE.Color(child.defaultColor);
      }
    });
    document.body.style.cursor = "default";

    if (tagged) {
      document.body.style.cursor = "pointer";
      tagged.object.material.color = new THREE.Color(0xffff00);
    }
    taggedControl = tagged;
  }

  if (tagged && !isHighlight && vueApp.activeMode.tagDetected)
    vueApp.activeMode.tagDetected(
      tagged.object.tag,
      tagged.object,
      isShift,
      intersectPoint
    );
  return tagged;
}

function onDocumentMouseMove(event) {
  if (
    vueApp.activeMode &&
    vueApp.activeMode.isActive &&
    vueApp.activeMode.mouseMove
  ) {
    let needHighlight = vueApp.activeMode.mouseMove(event);
    if (!needHighlight) {
      return;
    }
    if (vueApp.activeMode.tagDetected) {
      updateRaycasterFromEvent(event);
      findTaggedControl("group", true);
    }
  }

  // console.log("onDocumentMouseMove", event);
  if (dragPoint) {
    moveDragPoint(event);
  } else {
    updateRaycasterFromEvent(event);
    var tooth = intersectTooth();
    if (tooth) {
      let item = tooth.listItem;
      if (item && !item.isHovered) {
        allItems.forEach((x) => {
          if (item != x) {
            x.isHovered = false;
          }
        });
        item.isHovered = true;
        // highlightItem(item);
      }
    } else {
      allItems.forEach((x) => {
        x.isHovered = false;
      });
    }
  }
}

function onDocumentMouseUp(event) {
  // console.log("onDocumentMouseUp");
  if (vueApp.activeMode && vueApp.activeMode.isActive) {
    if (event.button && event.button > 0) {
      return;
    }
    let needSelection = true;
    if (vueApp.activeMode.mouseUp) {
      needSelection = vueApp.activeMode.mouseUp(event);
    }
    if (needSelection && vueApp.activeMode.toothSelected) {
      updateRaycasterFromEvent(event);
      let tooth = intersectTooth();
      vueApp.activeMode.toothSelected(tooth?.listItem, event);
    }
    return;
  }

  let now = new Date();
  let ts = now - mdTime;
  let upPoint = { x: event.clientX, y: event.clientY };
  let downUpDistance = getDistance2D(downPoint, upPoint);

  if (!isEditMode && !addToothGroup) return;

  updateRaycasterFromEvent(event);
  if (addToothGroup && event.button === 0 && ts < 200) {
    addingToothMouseUp();
  } else if (dragPoint) {
    endDragPoint();
  } else if (downUpDistance >= 0 && downUpDistance < 1) {
    if (selectedTooth && selectedTooth.listItem) {
      selectedTooth.listItem.isClicked = true;
    }

    if (event.button === 2 && selectedTooth) {
      changingToothMouseUp(event);
    } else if (event.button === 0 && ts < 400 && !viewMode.isFilledTeethView) {
      let tooth = intersectTooth();
      if (tooth) updateSelectedTooth(tooth);
    }
  }
  intersectedTooth = null;
  ioViewer.controls.enabled = true;
}

function onCameraChange() {
  // controls
  if (ioPlane) {
    ioPlane.lookAt(ioViewer.camera.position);
  }
}

function stopEvent(event, stopPropagation) {
  if (event) {
    if (event.preventDefault) {
      //  if (event.preventDefault != undefined) {
      event.preventDefault();
    }
    if (stopPropagation && event.stopPropagation) {
      event.stopPropagation();
    }
  }
}

//#endregion

//#region Adding tooth

function startAddingTooth(toothName, toothNumber) {
  console.log("startAddingTooth");
  addToothName = toothName;
  addToothNumber = toothNumber;
  document.getElementById("selectedToothHint").innerHTML = "";
  $(".intraoral-add-item").css("display", "block");
  isEditMode = false;

  let parentGroup = new THREE.Group();
  addToothGroup = new THREE.Group();
  parentGroup.add(addToothGroup);
  ioViewer.scene.add(parentGroup);

  if (selectedTooth) {
    if (selectedTooth.pointsGroup) selectedTooth.pointsGroup.visible = false;
    selectedTooth = null;
  }
}

function addingToothMouseUp() {
  if (addToothGroup) {
    let iogPoints = addToothGroup.children.filter(
      (item) => item.name === "point"
    );
    let length = iogPoints.length;
    let prevPoint = iogPoints[length - 1];

    let firstPointIntersected = length > 2 && intersectMesh(iogPoints[0]);
    if (firstPointIntersected) {
      let modelCameraDistance = addToothGroup.cameraDistance;
      let line = createLine(prevPoint, iogPoints[0], modelCameraDistance);
      addToothGroup.add(line);
      endAddingTooth(iogPoints);
    } else {
      addingToothAddPoint(prevPoint);
    }
  }
}

function addingToothAddPoint(prevPoint) {
  let groupPoint = intersectPoint(addToothGroup.parent);
  if (!groupPoint) {
    let scenePoint = intersectScene();
    if (scenePoint) {
      let modelCameraDistance = addToothGroup.cameraDistance;
      if (!modelCameraDistance) {
        modelCameraDistance = addToothGroup.cameraDistance = getDistance(
          ioViewer.camera.position,
          scenePoint
        );
      }
      let globalGraph = getGlobalGraph();
      let index1 = getPointIndex(globalGraph, scenePoint, 0.01);
      let node = globalGraph.getNode(index1);
      let ioPoint = createIoPoint(node.point, modelCameraDistance);
      ioPoint.gindex = index1;
      if (prevPoint) {
        let line = createLine(prevPoint, ioPoint, modelCameraDistance);
        addToothGroup.add(line);
      }
      addToothGroup.add(ioPoint);
    }
  }
}

function endAddingTooth(iogPoints) {
  let allPoints = [];
  let iogLines = addToothGroup.children.filter((item) => item.name === "line");
  iogLines.forEach((line) => {
    let linePoints = line.points.slice(1);
    linePoints.forEach((point) => {
      allPoints.push(point);
    });
  });
  let globalMesh = getGlobalMesh();
  let globalGraph = getGlobalGraph();
  // iogPoints.forEach((p) => (p.visible = false));

  document.body.style.cursor = "wait";

  setTimeout(function () {
    let color = null;
    let model = null;
    if (selectedTooth) {
      color = selectedTooth.color;
      model = selectedTooth;
    } else {
      color = getColorFromToothNumber(addToothNumber);
      model = {
        name: addToothName,
        baseNumber: 0,
        glbNumber: 0,
        number: addToothNumber,
        color: color, // "#00FF00",
        opacity: 1,
        isGingiva: false,
        isTooth: true,
        isUpper: globalMesh == upperMesh,
        isMaxilla: globalMesh == upperMesh,
        isAdded: true,
        isChanged: true,
        isVisible: true,
        recentlyAdded: true,
      };
      if (!model.name) model.name = getAddedToothName();
      model.pointsGroup = addToothGroup.parent;
    }

    updateBezierPoints(iogPoints, globalGraph);

    let geometry = getFacesGeometry(
      model,
      allPoints,
      globalMesh,
      globalGraph,
      ioViewer.scene,
      [] //getCrossedPoints()
    );
    if (geometry) {
      geometry.computeBoundingBox();
      let material = pongMaterial.clone();
      material.color = new THREE.Color(color);

      let mesh = new THREE.Mesh(geometry, material);
      // mesh.updateMatrix();
      if (selectedTooth) {
        selectedTooth.mainMesh.geometry =
          THREE.BufferGeometryUtils.mergeBufferGeometries([
            selectedTooth.mainMesh.geometry,
            geometry,
          ]);
        addToothGroup.innerGeometry = geometry;
        selectedTooth.isChanged = true;
        // selectedTooth.mainMesh.geometry.merge(mesh.geometry, mesh.matrix);
      } else {
        model.mesh = model.mainMesh = mesh;
        model.uuid = mesh.uuid;
        model.defaultBox = offsetBox(geometry.boundingBox);

        ioViewer.add_model(model);

        // ioViewer.scene.add(mesh);
        teethMeshes.push(mesh);
        teeth.push(model);
        allModels.push(model);
        selectedTooth = model;
        addItemInList(model);
      }
    } else {
      ioViewer.scene.remove(addToothGroup);
    }
    // console.log(faces);
    $(".intraoral-add-item").css("display", "none");
    document.getElementById("selectedToothHint").innerHTML = model.name;
    addToothGroup = null;
    isEditMode = true;
    document.body.style.cursor = "default";
  }, 100);
}

//#endregion

//#region Changing tooth

function updateSelectedTooth(tooth) {
  console.log("updateSelectedTooth", tooth);
  if (tooth) {
    // if (tooth == intersectedTooth) {
    if (selectedTooth != tooth) {
      selectedTooth = tooth;
      teeth.forEach((model) => {
        if (model.pointsGroup) {
          model.pointsGroup.visible = false;
          model.mesh.material.polygonOffsetFactor = 3;
        }
      });

      tooth.mesh.material.polygonOffsetFactor = 1;
      console.log(tooth.name);
      document.getElementById("selectedToothHint").innerHTML = tooth.name;

      updatePointsGroupVisibility(tooth, !selectedTooth.listItem.isFocused);
    }
    // }
  } else {
    selectedTooth = null;
    document.getElementById("selectedToothHint").innerHTML = "";
  }
}

function updatePointsGroupVisibility(model, visible) {
  const group = createPointsGroup(model);
  if (group) {
    group.visible = visible;
    if (visible) {
      let toothCameraDistance = getDistance(
        ioViewer.camera.position,
        model.defaultBox.center
      );

      let pointMeshes = getPointMeshesFromGroup(group);
      pointMeshes.forEach((p) => updateIoPointScale(p, toothCameraDistance));

      let lineMeshes = getLineMeshesFromGroup(group);
      lineMeshes.forEach((line) =>
        updateTubeCurve(line, line.points, toothCameraDistance)
      );
    }
  }
}

function changingToothMouseUp(event) {
  if (selectedTooth && !isChangingToothMouseUp) {
    isChangingToothMouseUp = true;
    console.log("changingToothMouseUp");
    let point = intersectPoint(selectedTooth.pointsGroup);
    if (!point) {
      changingToothAddPoint(event);
    } else {
      let mainPoints = getMainPoints(selectedTooth, point.parent);
      if (mainPoints.length > 3) {
        setNextPrevPoints(point, mainPoints);
        changingToothRemovePoint(point);
      }
    }
    selectedTooth.isChanged = true;
    setTimeout(function () {
      isChangingToothMouseUp = false;
    }, 400);
  }
  stopEvent(event);
}

function changingToothAddPoint(event) {
  if (selectedTooth && checkIsEditMode()) {
    updateRaycasterFromEvent(event);
    let intersectedLine = intersectSelectedBorder();
    let intersectedPoint1 = null;
    let intersectedPoint2 = null;
    if (intersectedLine) {
      let iparent = intersectedLine.parent;
      intersectedPoint1 = iparent.children.find(
        (c) => c.line1 == intersectedLine
      );
      intersectedPoint2 = iparent.children.find(
        (c) => c.line2 == intersectedLine
      );
      console.log(intersectedLine);
    }
    let point = intersectScene();
    if (point) {
      let areaPoints = [];
      let globalGraph = getGlobalGraph();

      let oldGeometry = selectedTooth.mesh.geometry.clone();
      let oldAllPoints = getAllBorderPointsArr(selectedTooth);
      let oldAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
      let oldAnchorGroup = getSavedAnchorGroup(selectedTooth);

      let newIndex = 0;
      let group = null;
      let mainPoints = null;
      if (intersectedPoint1 && intersectedPoint2) {
        group = intersectedPoint1.parent;

        mainPoints = getMainPoints(selectedTooth, group);
        newIndex = mainPoints.indexOf(intersectedPoint2);
      } else {
        let npoint = getNearestMainPoint(point);
        group = npoint.parent; //selectedTooth.pointsGroup;

        // npoint.material.color = new THREE.Color("#FF0000");
        mainPoints = getMainPoints(selectedTooth, group);
        newIndex = mainPoints.indexOf(npoint);

        setNextPrevPoints(npoint, mainPoints);
        let prevDist = getSquaredDistance(point, npoint.prevPoint.position);
        let nextDist = getSquaredDistance(point, npoint.nextPoint.position);
        if (prevDist > nextDist) {
          newIndex++;
        }
      }

      let modelCameraDistance = getModelCameraDistance(selectedTooth);
      let newPoint = createIoPoint(point, modelCameraDistance);
      mainPoints.splice(newIndex, 0, newPoint);
      setNextPrevPoints(newPoint, mainPoints);

      let index0 = newPoint.prevPoint.gindex;
      let index2 = newPoint.nextPoint.gindex;
      let index1 = getPointIndex(globalGraph, newPoint.position, 0.01);
      if (index1 == index2 || index1 == index0) {
        return;
      }

      let lineIndex = group.children.indexOf(newPoint.prevPoint.line1);
      group.remove(newPoint.prevPoint.line1);
      areaPoints = areaPoints.concat(newPoint.prevPoint.line1.points);

      let points1 = getShortPath(globalGraph, index1, index2);
      areaPoints = areaPoints.concat(points1.toReversed().slice(1));
      let line1 = createTubeCurve(points1, modelCameraDistance);
      group.children.splice(lineIndex, 0, line1);
      line1.parent = group;

      line1.point1 = points1[0];
      line1.point2 = points1[points1.length - 1];
      newPoint.line1 = line1;
      newPoint.position.copy(line1.point1);
      group.children.splice(lineIndex, 0, newPoint);
      newPoint.parent = group;
      newPoint.nextPoint.line2 = line1;

      let points2 = getShortPath(globalGraph, index0, index1);
      areaPoints = areaPoints.concat(points2.slice(1).toReversed().slice(1));
      let line2 = createTubeCurve(points2, modelCameraDistance);
      group.children.splice(lineIndex, 0, line2);
      line2.parent = group;
      line2.point1 = points2[0];
      line2.point2 = points2[points2.length - 1];
      newPoint.line2 = line2;
      newPoint.prevPoint.line1 = line2;

      document.body.style.cursor = "wait";

      updateBezierPoints(mainPoints, globalGraph);

      // let tooth = intersectTooth();
      // let isClearing = tooth == selectedTooth;

      let rootPoint = newPoint.position.clone();

      let intersectedPoint = intersectFirst(
        selectedTooth.mesh,
        ioRaycaster,
        rootPoint
      );
      let isClearing = !!intersectedPoint;

      // if (!isClearing)
      //   fillArea(
      //     selectedTooth,
      //     areaPoints,
      //     rootPoint,
      //     globalGraph,
      //     ioViewer.scene,
      //     isClearing
      //   );

      let newGeometry = selectedTooth.mesh.geometry.clone();
      let newAllPoints = getAllBorderPointsArr(selectedTooth);
      let newAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
      let newAnchorGroup = getSavedAnchorGroup(selectedTooth);
      let change = {
        model: selectedTooth,
        oldGeometry: oldGeometry,
        oldPoints: oldAllPoints,
        oldAnchorPoints: oldAnchorPoints,
        oldAnchorGroup: oldAnchorGroup,
        newGeometry: newGeometry,
        newPoints: newAllPoints,
        newAnchorPoints: newAnchorPoints,
        newAnchorGroup: newAnchorGroup,
      };
      console.log("changingToothAddPoint ioChange", change);
      addIoChange(change);

      document.body.style.cursor = "default";
      return;
      setTimeout(function () {
        let allPoints = getAllBorderPoints(selectedTooth);
        let globalMesh = getGlobalMesh();
        let geometry = getFacesGeometry(
          selectedTooth,
          allPoints,
          globalMesh,
          globalGraph,
          ioViewer.scene,
          getCrossedPoints()
        );
        if (geometry) {
          selectedTooth.mesh.geometry.dispose();
          selectedTooth.mesh.geometry = geometry;
        }
        document.body.style.cursor = "default";
      }, 100);
    }
  }
}

function changingToothRemovePoint(point) {
  if (selectedTooth && checkIsEditMode()) {
    clearTempMeshes(ioViewer.scene);
    let areaPoints = [];

    let mainPoints = getMainPoints(selectedTooth, point.parent);

    let oldGeometry = selectedTooth.mesh.geometry.clone();
    let oldAllPoints = getAllBorderPointsArr(selectedTooth);
    let oldAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
    let oldAnchorGroup = getSavedAnchorGroup(selectedTooth);

    let pindex = mainPoints.indexOf(point);
    mainPoints.splice(pindex, 1);
    let group = point.parent;
    let lineIndex = group.children.indexOf(point.line2);
    group.remove(point);
    group.remove(point.line1);
    group.remove(point.line2);
    // let tubeRadius = point.line1.tubeRadius;

    areaPoints = areaPoints.concat(point.line2.points);
    areaPoints = areaPoints.concat(point.line1.points.slice(1));

    let p1 = point.prevPoint;
    let p2 = point.nextPoint;
    let globalGraph = getGlobalGraph();
    let points = getShortPath(globalGraph, p1.gindex, p2.gindex);

    areaPoints = areaPoints.concat(points.slice(1).toReversed().slice(1));

    let modelCameraDistance = getModelCameraDistance(selectedTooth);
    let newLine = createTubeCurve(points, modelCameraDistance);
    newLine.points = newLine.shortPoints = points;
    newLine.point1 = p1.position;
    newLine.point2 = p2.position;
    p1.line1 = newLine;
    p2.line2 = newLine;
    group.children.splice(lineIndex, 0, newLine);
    newLine.parent = group;

    document.body.style.cursor = "wait";

    updateBezierPoints(mainPoints, globalGraph);

    let isClearing = true;
    let middlePoint = points[Math.floor(points.length / 2)];
    if (middlePoint) {
      let origin = ioViewer.camera.position.clone();
      // let origin = new THREE.Vector3(
      //   middlePoint.x,
      //   middlePoint.y + 10,
      //   middlePoint.z
      // );
      let target = middlePoint.clone();
      let direction = target.sub(origin).normalize();
      updateIoRaycaster(origin, direction);

      let intersectedPoint = intersectFirst(
        selectedTooth.mesh,
        ioRaycaster,
        middlePoint
      );
      isClearing = !!intersectedPoint;
      console.log("changingToothRemovePoint ", isClearing);
      // if (intersectedPoint) {
      //   drawSpherePoint(ioViewer.scene, intersectedPoint, 0x0000ff);
      // } else {
      //   drawSpherePoint(ioViewer.scene, middlePoint);
      // }
    }

    // if (!isClearing)
    //   fillArea(
    //     selectedTooth,
    //     areaPoints,
    //     middlePoint,
    //     globalGraph,
    //     ioViewer.scene,
    //     isClearing
    //   );

    let newGeometry = selectedTooth.mesh.geometry.clone();
    let newAllPoints = getAllBorderPointsArr(selectedTooth);
    let newAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
    let newAnchorGroup = getSavedAnchorGroup(selectedTooth);
    let change = {
      model: selectedTooth,
      oldGeometry: oldGeometry,
      oldPoints: oldAllPoints,
      oldAnchorPoints: oldAnchorPoints,
      oldAnchorGroup: oldAnchorGroup,
      newGeometry: newGeometry,
      newPoints: newAllPoints,
      newAnchorPoints: newAnchorPoints,
      newAnchorGroup: newAnchorGroup,
    };
    // console.log("changingToothRemovePoint ioChange", change);
    addIoChange(change);

    document.body.style.cursor = "default";
    return;

    setTimeout(function () {
      let allPoints = getAllBorderPoints(selectedTooth);
      let globalMesh = getGlobalMesh();
      let geometry = getFacesGeometry(
        selectedTooth,
        allPoints,
        globalMesh,
        globalGraph,
        ioViewer.scene,
        getCrossedPoints()
      );
      if (geometry) {
        selectedTooth.mesh.geometry.dispose();
        selectedTooth.mesh.geometry = geometry;
      }
      document.body.style.cursor = "default";
    }, 100);
  }
}

//#endregion

//#region Drag point

function startDragPoint(point, event) {
  ioViewer.controls.enabled = false;

  if (event.button === 0 && checkIsEditMode()) {
    let mainPoints = getMainPoints(selectedTooth, point.parent);
    setNextPrevPoints(point, mainPoints);
    dragPoint = point;
    dragPoint.basePosition = point.position.clone();
    dragPoint.baseIndex = getPointIndex(
      getGlobalGraph(),
      dragPoint.basePosition
    );
    if (event.preventDefault) event.preventDefault();
  }
}

function moveDragPoint(event) {
  if (dragPoint) {
    updateRaycasterFromEvent(event);
    let point = intersectScene();
    if (point) {
      dragPoint.position.copy(point);

      if (dragPoint.line1) {
        let line = dragPoint.line1;
        updateTubeLine(line, point, line.point2);
        line.point1 = point;
      }

      if (dragPoint.line2) {
        let line = dragPoint.line2;
        updateTubeLine(line, line.point1, point);
        line.point2 = point;
      }
    }
  }
}

function endDragPoint() {
  clearTempMeshes(ioViewer.scene);

  if (dragPoint) {
    let globalGraph = getGlobalGraph();
    let index1 = getPointIndex(globalGraph, dragPoint.position, 0.01);
    let newPosition = globalGraph.getNode(index1).position;
    if (
      index1 === dragPoint.baseIndex ||
      areNearPoints(dragPoint.basePosition, newPosition)
    ) {
      let line = dragPoint.line1;
      updateTubeCurve(
        line,
        line.bezierPoints ? line.bezierPoints : line.points
      );
      line = dragPoint.line2;
      updateTubeCurve(
        line,
        line.bezierPoints ? line.bezierPoints : line.points
      );
      dragPoint.position.copy(dragPoint.basePosition);
      dragPoint = null;
      return;
    }

    let index0 = dragPoint.prevPoint.gindex;
    let index2 = dragPoint.nextPoint.gindex;

    if (index1 == index2 || index1 == index0) {
      return;
    }

    let areaPoints = [];
    let oldPoints = [];
    let newPoints = [];
    let oldGeometry = selectedTooth.mesh.geometry.clone();
    let oldAllPoints = getAllBorderPointsArr(selectedTooth);
    let oldAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
    let oldAnchorGroup = getSavedAnchorGroup(selectedTooth);

    dragPoint.basePosition = null;

    let tbPoints = getShortPath(globalGraph, index0, index2);
    tbPoints = tbPoints.slice(1).toReversed().slice(1);

    let oldPoints1 = dragPoint.line2.shortPoints
      ? dragPoint.line2.shortPoints
      : dragPoint.line2.points;
    areaPoints = areaPoints.concat(oldPoints1);
    let oldPoints2 = dragPoint.line1.shortPoints
      ? dragPoint.line1.shortPoints
      : dragPoint.line1.points;
    areaPoints = areaPoints.concat(oldPoints2.slice(1));
    oldPoints = areaPoints;

    let line = dragPoint.line1;
    line.points = line.shortPoints = getShortPath(globalGraph, index1, index2);
    areaPoints = areaPoints.concat(line.points.toReversed().slice(1));
    updateTubeCurve(line, line.points);
    line.point1 = line.points[0];

    dragPoint.position.copy(line.point1);

    line = dragPoint.line2;
    line.points = line.shortPoints = getShortPath(globalGraph, index0, index1);
    areaPoints = areaPoints.concat(line.points.slice(1).toReversed().slice(1));
    updateTubeCurve(line, line.points);
    line.point2 = line.points[line.points.length - 1];

    let mainPoints = getMainPoints(selectedTooth, dragPoint.parent);
    updateBezierPoints(mainPoints, globalGraph);

    newPoints = newPoints.concat(dragPoint.line2.points);
    newPoints = newPoints.concat(dragPoint.line1.points.slice(1));

    // oldPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0xff0000, 0.05));
    // newPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0x0000ff, 0.05));
    // tbPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0xffff00, 0.05));

    let crossPoints = [];
    let newLength = newPoints.length;
    let oldLenth = oldPoints.length;
    oldPoints.forEach((oldPoint, oldIndex) => {
      newPoints.forEach((newPoint, newIndex) => {
        if (
          oldIndex != newIndex &&
          newLength - newIndex != oldLenth - oldIndex &&
          areNearPoints(oldPoint, newPoint)
        ) {
          crossPoints.push(oldPoint);
          // drawPoint(oldPoint, ioViewer.scene, 0x00ff00, 0.05);
          return;
        }
      });
    });
    // console.log("crossPoints", crossPoints.length);

    oldPoints = oldPoints.concat(tbPoints);
    newPoints = newPoints.concat(tbPoints);

    // oldPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0xff0000, 0.05));
    let groupIndex = selectedTooth.pointsGroup.children.indexOf(
      dragPoint.parent
    );

    let rootPoint = dragPoint.position.clone();
    dragPoint = null;

    document.body.style.cursor = "wait";

    let intersectedPoint = intersectFirst(
      selectedTooth.mesh,
      ioRaycaster,
      rootPoint
    );
    let isClearing = !!intersectedPoint;
    let model = selectedTooth;
    let scene = ioViewer.scene;
    console.log(
      "endDragPoint isClearing crossPoints",
      isClearing,
      crossPoints.length
    );

    setTimeout(function () {
      // if (groupIndex <= 0) {
      //   if (crossPoints.length > 0) {
      //     // newPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0x00ff00, 0.05));
      //     fillArea(
      //       model,
      //       oldPoints,
      //       rootPoint,
      //       globalGraph,
      //       scene,
      //       !isClearing
      //     );
      //     fillArea(model, newPoints, rootPoint, globalGraph, scene, isClearing);
      //   } else {
      //     // areaPoints.forEach((p) => drawPoint(p, ioViewer.scene, 0x00ff00, 0.05));
      //     fillArea(
      //       model,
      //       areaPoints,
      //       rootPoint,
      //       globalGraph,
      //       scene,
      //       isClearing
      //     );
      //   }
      // }

      let newGeometry = selectedTooth.mesh.geometry.clone();
      let newAllPoints = getAllBorderPointsArr(selectedTooth);
      let newAnchorPoints = getSavedAnchorPointsArr(selectedTooth);
      let newAnchorGroup = getSavedAnchorGroup(selectedTooth);
      let change = {
        model: selectedTooth,
        oldGeometry: oldGeometry,
        oldPoints: oldAllPoints,
        oldAnchorPoints: oldAnchorPoints,
        oldAnchorGroup: oldAnchorGroup,
        newGeometry: newGeometry,
        newPoints: newAllPoints,
        newAnchorPoints: newAnchorPoints,
        newAnchorGroup: newAnchorGroup,
      };
      addIoChange(change);
      selectedTooth.isChanged = true;
      document.body.style.cursor = "default";
    }, 100);
    return;

    setTimeout(function () {
      let allPoints = getAllBorderPoints(selectedTooth);
      let globalMesh = getGlobalMesh();
      let geometry = getFacesGeometry(
        selectedTooth,
        allPoints,
        globalMesh,
        globalGraph,
        ioViewer.scene,
        getCrossedPoints()
      );
      if (geometry) {
        selectedTooth.mesh.geometry.dispose();
        selectedTooth.mesh.geometry = geometry;
      }
      document.body.style.cursor = "default";
    }, 100);
  }
}

//#endregion

//#region Get logic

function getGlobalMesh() {
  return isUpperMode ? upperMesh : lowerMesh;
}

function getGlobalBox() {
  return isUpperMode ? upperBox : lowerBox;
}

function getGlobalGraph() {
  return isUpperMode ? upperGraph : lowerGraph;
}

function getCrossedPoints() {
  return isUpperMode ? upperCrossedPoints : lowerCrossedPoints;
}

function getModelByName(name, notDeleted) {
  if (name && allModels.length) {
    return allModels.find(
      (m) => m.name === name && (!notDeleted || !m.isDeleted)
    );
  }
}

function getModelByItem(item, notDeleted) {
  if (item && allModels.length) {
    return allModels.find(
      (m) => m.listItem === item && (!notDeleted || !m.isDeleted)
    );
  }
}

function getToothByUuid(uuid) {
  return uuid ? teeth.find((x) => x.mesh.uuid === uuid && !x.isDeleted) : null;
}

function getNearestMainPoint(newPoint) {
  if (selectedTooth) {
    let npoint = null;
    let points = getMainPoints(selectedTooth);
    let minDistance = Infinity;
    points.forEach((mesh) => {
      let distance = getSquaredDistance(newPoint, mesh.position);
      if (distance < minDistance) {
        minDistance = distance;
        npoint = mesh;
      }
    });
    return npoint;
  }
}

function getSquaredDistance(p1, p2) {
  const dx = p1.x - p2.x,
    dy = p1.y - p2.y,
    dz = p1.z - p2.z;

  return dx * dx + dy * dy + dz * dz;
}

function getAddedToothName() {
  let prefix = "tooth_" + addedSuffix;
  if (isUpperMode) {
    prefix += upperSuffix;
  } else {
    prefix += lowerSuffix;
  }
  for (let i = 1; ; i++) {
    let tempName = prefix + i;
    let existedTooth = teeth.find((t) => t.name === tempName);
    if (!existedTooth) {
      return tempName;
    }
  }
}

//#endregion

//#region Intersect logic

function updateRaycasterFromEvent(event) {
  try {
    let direction = getIoMouse3dDirection(event);
    let origin = ioViewer.camera.position;
    ioRaycaster.set(origin, direction);
  } catch (error) {
    // console.error("updateRaycasterFromEvent", error);
  }
}

function updateIoRaycaster(origin, direction) {
  try {
    ioRaycaster.set(origin, direction);
  } catch (error) {
    console.error("updateRaycaster", error);
  }
}

function getIoMouse3dDirection(event) {
  let direction = null;
  if (event && event.pageX && event.pageY) {
    let intraoralContainer = document.getElementById("intraoralContainer");
    let overlayRect = intraoralContainer.getBoundingClientRect();
    let x = event.pageX - overlayRect.left;
    let y = event.pageY - overlayRect.top;
    direction = new THREE.Vector3(
      (x / overlayRect.width) * 2 - 1,
      -(y / overlayRect.height) * 2 + 1,
      0.5
    );
    direction.unproject(ioViewer.camera);
    direction.sub(ioViewer.camera.position);
    direction.normalize();
  }
  return direction;
}

function getAllMeshesFromGroup(pointsGroup, borderGroup) {
  let meshes = [];
  for (let childGroup of pointsGroup.children) {
    meshes.push(...childGroup.children);
  }
  return typeof borderGroup !== "undefined"
    ? meshes.filter((m) => m.parent === borderGroup)
    : meshes;
}

function getPointMeshesFromGroup(pointsGroup, borderGroup) {
  let meshes = [];
  for (let childGroup of pointsGroup.children) {
    meshes.push(...childGroup.children.filter((item) => item.name === "point"));
  }
  return typeof borderGroup !== "undefined"
    ? meshes.filter((m) => m.parent === borderGroup)
    : meshes;
}

function getLineMeshesFromGroup(pointsGroup, borderGroup) {
  let meshes = [];
  for (let childGroup of pointsGroup.children) {
    meshes.push(...childGroup.children.filter((item) => item.name != "point"));
  }
  return typeof borderGroup !== "undefined"
    ? meshes.filter((m) => m.parent === borderGroup)
    : meshes;
}

function intersectPoint(pointsGroup) {
  let pointMesh = null;
  if (pointsGroup) {
    let meshes = getPointMeshesFromGroup(pointsGroup);
    // pointsGroup.children.filter((item) => item.name === "point");
    let globalMesh = getGlobalMesh();
    // meshes.push(globalMesh);
    let intersects = ioRaycaster.intersectObjects(meshes);
    if (intersects.length > 0) {
      let firstMesh = intersects[0].object;
      // console.log(firstMesh.name);
      if (firstMesh != globalMesh) {
        pointMesh = firstMesh;
      }
    }
  }
  return pointMesh;
}

function intersectPointsGroup(pointsGroup) {
  let pointMesh = null;
  if (pointsGroup) {
    let meshes = getAllMeshesFromGroup(pointsGroup);
    let globalMesh = getGlobalMesh();
    // meshes.push(globalMesh);
    let intersects = ioRaycaster.intersectObjects(meshes);
    if (intersects.length > 0) {
      let firstMesh = intersects[0].object;
      // console.log(firstMesh.name);
      if (firstMesh != globalMesh) {
        pointMesh = firstMesh;
      }
    }
  }
  return pointMesh;
}

function intersectTooth(raycaster) {
  if (!raycaster) {
    raycaster = ioRaycaster;
  }

  let tooth = null;
  let tMeshes = teeth.map((t) => t.mesh);
  let intersects = raycaster.intersectObjects(tMeshes); // ioViewer.scene.children);
  if (intersects.length > 0) {
    intersects.find((intersect) => {
      let objectUuid = intersect.object.uuid;
      let temp = getToothByUuid(objectUuid);
      if (temp && !temp.isDeleted && temp.mesh.visible) tooth = temp;
      return tooth != null;
    });
  }
  return tooth;
}

function intersectScene() {
  let point = null;
  let intersects = ioRaycaster.intersectObject(getGlobalMesh());
  if (intersects.length > 0) {
    point = intersects[0].point;
  }
  return point;
}

function intersectSelectedBorder() {
  let lineMesh = null;

  if (
    selectedTooth &&
    selectedTooth.pointsGroup &&
    selectedTooth.pointsGroup.visible
  ) {
    let meshes = getLineMeshesFromGroup(selectedTooth.pointsGroup);
    let intersects = ioRaycaster.intersectObjects(meshes);

    if (intersects.length > 0) {
      // point = intersects[0].point;
      // drawPoint(point, ioViewer.scene);
      return intersects[0].object;
    }
  }

  return lineMesh;
}

function intersectMesh(mesh, raycaster) {
  let point = null;
  if (!raycaster) {
    raycaster = ioRaycaster;
  }
  if (mesh) {
    let intersects = raycaster.intersectObject(mesh);
    if (intersects.length > 0) {
      point = intersects[0].point;
    }
  }
  return point;
}

function intersectFirst(mesh, raycaster, targetPoint) {
  let point = null;
  if (!raycaster) {
    raycaster = ioRaycaster;
  }
  if (mesh) {
    let intersects = raycaster.intersectObject(mesh);
    // let globalMesh = getGlobalMesh();
    //.intersectObjects([mesh, globalMesh]);
    for (let intersect of intersects) {
      let dist = getDistance(targetPoint, intersect.point);
      if (dist < 0.999) {
        point = intersect.point;
      }
    }
  }
  return point;
}

//#endregion

//#region Points

const minDistance = 2;

function createPointsGroupBase(model) {
  if (!model.pointsGroup && model.points) {
    const modelCameraDistance = getModelCameraDistance(model);

    let globalGraph = getGlobalGraph();
    model.pointsGroup = new THREE.Group();
    model.pointsGroup.visible = false;

    const group = new THREE.Group();
    model.pointsGroup.add(group);
    model.mainPoints = [];

    const points = model.points.map(
      (p) => new THREE.Vector3(p[0], p[1], p[2] + globalDz)
    );

    const anchorPoints = null;
    const newAnchorPoints = [];

    if (points.length > 1) {
      let distance = 0;
      let indexDif = 0;
      points.forEach((ppp, index) => {
        if (index != 0) {
          distance += getDistance(points[index - 1], points[index]);
          indexDif++;
        }
        if (index == points.length - 1) {
          distance = minDistance;
        }
        let adistance =
          anchorPoints && anchorPoints.length
            ? getDistance(anchorPoints[0], points[index])
            : 0;
        if (anchorPoints && !anchorPoints.length && index != points.length - 1)
          return;

        if (
          (anchorPoints && adistance < 0.01) ||
          (!anchorPoints && distance >= minDistance)
        ) {
          if (anchorPoints) anchorPoints.shift();
          let subset = points.slice(index - indexDif, index + 1);
          distance = 0;
          indexDif = 0;
          let point1 = subset[0];

          if (index == points.length - 1) {
            subset.push(points[0]);
          }

          let point2 = subset[subset.length - 1];

          if (subset.length < 2) {
            console.log("wrong subset");
            return;
          }

          let line = createTubeCurve(subset, modelCameraDistance);
          line.point1 = point1;
          line.point2 = point2;
          line.points = subset;

          let index1 = getPointIndex(globalGraph, point1);
          let index2 = getPointIndex(globalGraph, point2);
          let tempSubset = getShortPath(globalGraph, index1, index2);
          if (tempSubset.length > 1) {
            line.shortPoints = tempSubset;
            line.bezierPoints = tempSubset;
            subset = tempSubset;
            point1 = subset[0];
            point2 = subset[subset.length - 1];
          }

          let color = 0x00ff00;
          // if (index === 0) {
          //   color = 0xff0000;
          // }
          // if (index === 10) {
          //   color = 0x0000ff;
          // }

          let point = createIoPoint(point1, modelCameraDistance);
          newAnchorPoints.push([point1.x, point1.y, point1.z]);

          point.line1 = line;
          point.line2 = group.children.slice(-1)[0];
          model.mainPoints.push(point);

          group.add(point, line);
        }
      });
      group.children[0].line2 = group.children.slice(-1)[0];
      console.log("newAnchorPoints", newAnchorPoints);
      model.anchorPoints = model.savedAnchorPoints = newAnchorPoints;
      model.anchorGroup = model.savedAnchorGroup = [newAnchorPoints];

      updateBezierPoints(model.mainPoints, globalGraph);
    } else {
      console.log("invalid points for model", model);
    }

    model.pointsGroup.visible = false;
    ioViewer.scene.add(model.pointsGroup);
  }
  return model.pointsGroup;
}

function getModelCameraDistance(model) {
  let modelCameraDistance = 0;
  if (ioViewer && ioViewer.camera && model.defaultBox) {
    modelCameraDistance = getDistance(
      ioViewer.camera.position,
      model.defaultBox.center
    );
  }
  return modelCameraDistance;
}

function createPointsGroup(model) {
  if (!model.pointsGroup) {
    if (!model.anchorPoints || !model.anchorGroup) {
      //  || !vueApp.isEngineerMode
      return createPointsGroupBase(model);
    }

    const modelCameraDistance = getModelCameraDistance(model);
    const globalGraph = getGlobalGraph();
    model.pointsGroup = new THREE.Group();
    model.pointsGroup.visible = false;

    let i = 0;
    for (let grPoints of model.anchorGroup) {
      if (grPoints.length < 3) {
        continue;
      }
      const group = new THREE.Group();
      model.pointsGroup.add(group);

      const groupMainPoints = [];
      const anchorPoints = grPoints.map(
        (p) => new THREE.Vector3(p[0], p[1], p[2] + globalDz)
      );
      // let firstAnchor = anchorPoints ? anchorPoints.shift() : null;

      if (anchorPoints.length > 1) {
        anchorPoints.forEach((ppp, index) => {
          let point1 = anchorPoints[index];
          let point2 = anchorPoints[index + 1];
          if (!point2) {
            point2 = anchorPoints[0];
          }
          let anchorDistance = getDistance(point1, point2);
          // console.log("anchorDistance", anchorDistance);

          let index1 = getPointIndex(globalGraph, point1);
          let index2 = getPointIndex(globalGraph, point2);
          let subset = getShortPath(globalGraph, index1, index2);
          if (subset.length > 1) {
            // let tubeRadius = anchorDistance < 0.7 ? 0.025 : 0.05;
            let line = createTubeCurve(subset, modelCameraDistance);
            point1 = subset[0];
            point2 = subset[subset.length - 1]
            line.point1 = point1;
            line.point2 = point2;
            line.points = subset;
            // line.tubeRadius = tubeRadius;

            line.shortPoints = subset;
            line.bezierPoints = subset;

            // let color = 0x00ff00;
            // let pointRadius = anchorDistance < 0.7 ? 0.05 : 0.1;
            let point = createIoPoint(point1, modelCameraDistance);
            point.line1 = line;
            point.line2 = group.children.slice(-1)[0];
            groupMainPoints.push(point);

            group.add(point, line);
          }
        });
        group.children[0].line2 = group.children.slice(-1)[0];

        updateBezierPoints(groupMainPoints, globalGraph);
      } else {
        console.log("invalid points for model", model);
      }
      i++;
    }
    ioViewer.scene.add(model.pointsGroup);
  }
  return model.pointsGroup;
}

function setNextPrevPoints(point, points) {
  if (point.parent) points = points.filter((p) => p.parent == point.parent);

  let pindex = points.indexOf(point);
  point.prevPoint = points[pindex - 1];
  if (!point.prevPoint) {
    point.prevPoint = points[points.length - 1];
  }
  point.nextPoint = points[pindex + 1];
  if (!point.nextPoint) {
    point.nextPoint = points[0];
  }
  let globalGraph = getGlobalGraph();
  point.prevPoint.gindex = getPointIndex(globalGraph, point.prevPoint.position);
  point.nextPoint.gindex = getPointIndex(globalGraph, point.nextPoint.position);
}

function createLine(prevPoint, nextPoint, modelCameraDistance) {
  let globalGraph = getGlobalGraph();
  let index0 = prevPoint.gindex;
  let index1 = nextPoint.gindex;
  let pathPoints = getShortPath(globalGraph, index0, index1);
  let line = createTubeCurve(pathPoints, modelCameraDistance);
  line.point1 = prevPoint.position;
  line.point2 = nextPoint.position;
  line.points = line.shortPoints = pathPoints;
  prevPoint.line1 = line;
  nextPoint.line2 = line;
  return line;
}

function getAllBorderPoints(tooth) {
  let allPoints = [];
  if (tooth) {
    if (!tooth.pointsGroup) createPointsGroup(tooth);
    getLineMeshesFromGroup(tooth.pointsGroup).forEach((mesh) => {
      if (mesh.name != "point") {
        let line = mesh;
        let linePoints = line.shortPoints ? line.shortPoints : line.points;
        //line.bezierPoints ? line.bezierPoints : line.points;
        allPoints.push(...linePoints.slice(1));
      }
    });
  }
  return allPoints;
}

function getAllBorderPointsArr(tooth) {
  let allPoints = [];
  if (tooth && (tooth.points || tooth.pointsGroup)) {
    if (!tooth.pointsGroup) {
      allPoints = tooth.points;
    } else {
      getLineMeshesFromGroup(tooth.pointsGroup).forEach((mesh) => {
        if (mesh.name != "point") {
          let line = mesh;
          let linePoints = line.shortPoints ? line.shortPoints : line.points;
          allPoints.push(...linePoints.slice(1).map((p) => [p.x, p.y, p.z]));
        }
      });
    }
  }
  return allPoints;
}

function getSavedBorderPointsArr(tooth) {
  let allPoints = [];
  if (tooth && (tooth.points || tooth.pointsGroup)) {
    if (!tooth.pointsGroup) {
      allPoints = tooth.points;
    } else {
      getLineMeshesFromGroup(tooth.pointsGroup).forEach((mesh) => {
        if (mesh.name != "point") {
          let line = mesh;
          let linePoints = line.bezierPoints ? line.bezierPoints : line.points;
          allPoints.push(...linePoints.slice(1).map((p) => [p.x, p.y, p.z]));
        }
      });
    }
  }
  return allPoints;
}

function getSavedAnchorPointsArr(tooth) {
  let aPoints = [];
  if (tooth && (tooth.points || tooth.pointsGroup)) {
    if (!tooth.pointsGroup) {
      aPoints = tooth.anchorPoints;
    } else {
      getPointMeshesFromGroup(tooth.pointsGroup).forEach((mesh) => {
        if (mesh.name == "point") {
          let p = mesh.basePosition ? mesh.basePosition : mesh.position;
          aPoints.push([p.x, p.y, p.z]);
        }
      });
    }
  }
  return aPoints;
}

function getSavedAnchorGroup(tooth) {
  let anchorGroup = null;
  if (tooth && (tooth.anchorGroup || tooth.pointsGroup)) {
    if (!tooth.pointsGroup) {
      anchorGroup = tooth.anchorGroup;
    } else {
      anchorGroup = [];
      for (let childGroup of tooth.pointsGroup.children) {
        let points = childGroup.children.filter(
          (item) => item.name === "point"
        );
        let aPoints = [];
        points.forEach((mesh) => {
          let p = mesh.basePosition ? mesh.basePosition : mesh.position;
          aPoints.push([p.x, p.y, p.z]);
        });
        anchorGroup.push(aPoints);
      }
    }
  }
  return anchorGroup;
}

function getMainPoints(tooth, borderGroup) {
  if (tooth) {
    if (!tooth.pointsGroup) createPointsGroup(tooth);
    let mainPoints = getPointMeshesFromGroup(tooth.pointsGroup, borderGroup);
    return mainPoints;
  }
}

function smoothSelectedToothBorder() {
  if (selectedTooth) {
    document.body.style.cursor = "wait";
    document.getElementById("intraoralSmoothBtn").style.cursor = "wait";

    let model = selectedTooth;
    setTimeout(() => {
      let mainPoints = getMainPoints(model);

      let globalGraph = getGlobalGraph();
      mainPoints.forEach((p1, index) => {
        let p2 = mainPoints[index + 1];
        if (!p2) p2 = mainPoints[0];
        let index1 = getPointIndex(globalGraph, p1.position);
        let index2 = getPointIndex(globalGraph, p2.position);
        let points = getShortPath(globalGraph, index1, index2);
        let line = p1.line1;
        line.point1 = p1;
        line.point2 = p2;
        line.points = line.shortPoints = points;
        updateTubeCurve(line, points);
      });

      document.body.style.cursor = "default";
      document.getElementById("intraoralSmoothBtn").style.cursor = "pointer";
    }, 100);
  }
}

function repairIntraoralColouring() {
  if (selectedTooth) {
    let repairBtn = document.getElementById("intraoralRepairBtn");
    document.body.style.cursor = "wait";
    if (repairBtn) repairBtn.style.cursor = "wait";

    let model = selectedTooth;
    let jsonData = {};
    let sPoints = getSavedBorderPointsArr(model);
    let allPoints = getAllBorderPointsArr(model);
    jsonData[model.name] = sPoints;
    jsonData["teeth"] = [model.name];
    if (model.crossedTeeth && model.crossedTeeth.length) {
      jsonData["crossedTeeth"] = model.crossedTeeth;
    } else {
      jsonData["crossedTeeth"] = [];
      // allModels.filter(m => m.isUpper == isUpperMode && m.isTooth && !m.isAdded).map(m => m.name);
      // && !m.isDeleted
    }

    const jsonBlob = new Blob([JSON.stringify(jsonData)], {
      type: "application/json",
    });
    sendApplyBorderPointsRequest(
      jsonBlob,
      upperMesh && lowerMesh,
      model.isUpper,
      (zipBlob) => {
        document.body.style.cursor = "default";
        if (repairBtn) repairBtn.style.cursor = "pointer";

        if (!zipBlob) {
          return;
        }
        // var zip = new JSZip();
        JSZip.loadAsync(zipBlob).then((zip) => {
          console.log(zip.files);
          zip.forEach(function (fileName, file) {
            let modelName = fileName.replace(EXTR_TAG, "");
            let tooth = getModelByName(modelName);
            if (tooth) {
              let zipFile = zip.file(fileName);
              zipFile.async("arraybuffer").then((buffer) => {
                getGeometryFromStl(buffer, (elementGeometry) => {
                  const tempGeo = new THREE.Geometry().fromBufferGeometry(
                    elementGeometry
                  );
                  tempGeo.mergeVertices();
                  tempGeo.computeVertexNormals();
                  elementGeometry = new THREE.BufferGeometry().fromGeometry(
                    tempGeo
                  );
                  elementGeometry.computeBoundingBox();

                  let box = elementGeometry.boundingBox;
                  box = offsetBox(box, 0, 0, 0);
                  tooth.defaultBox = box;
                  tooth.defaultPosition = {
                    x: 0, // box.center.x,
                    y: 0, // box.center.y,
                    z: 0, // box.center.z,
                  };

                  let geometry = prepareGeometry(elementGeometry, false);

                  // let change = {
                  //   model: selectedTooth,
                  //   oldGeometry: tooth.mesh.geometry.clone(),
                  //   oldPoints: allPoints,
                  //   newGeometry: geometry.clone(),
                  //   newPoints: allPoints,
                  // };
                  // addIoChange(change);

                  tooth.mesh.geometry.dispose();
                  tooth.mesh.geometry = geometry;
                });
              });
            }
          });
        });
      }
    );
  }
}

function updateOpacity(opacityValue) {
  allModels.forEach((model) => {
    ioViewer.set_opacity(model.id, opacityValue);
  });
}

//#endregion

//#region Modes/Camera

function checkIsEditMode() {
  if (!isEditMode) {
    swal({
      text: "Cannot edit in this mode. Please switch to edit mode for this.",
    });
  }
  return isEditMode;
}

function isAnyToothFocused() {
  return teeth.find((t) => t.isFocused || (t.listItem && t.listItem.isFocused));
}

function isAnyModelFocused() {
  return allModels.find(
    (t) => t.isFocused || (t.listItem && t.listItem.isFocused)
  );
}

function switchViewModes(isUpper, isLower) {
  let previosDouble = isDoubleMode;

  isDoubleMode = isUpper && isLower;
  isEditMode = viewMode.isEdit && !isDoubleMode && !vueApp.isViewMode;
  if (isUpperMode != isUpper) {
    isUpperMode = isUpper;
    selectedTooth = null;
    updateSelectedTooth(null);
  }
  allModels.forEach((model) => {
    model.mesh.visible =
      model.isVisible &&
      !model.isDeleted &&
      (model.isUpper === isUpper || isDoubleMode);
    if (model.pointsGroup) {
      model.pointsGroup.visible = false;
      model.mesh.material.polygonOffsetFactor = 3;
    }
  });
  resetIoCamera();

  if (isUpperMode) rotateIntraoralToView(2);

  if (previosDouble != isDoubleMode) {
    let scaleMatrix = new THREE.Matrix4().makeScale(1, 1, -1);
    let scaleInvert = scaleMatrix.clone().invert();
    let translationMatrix = new THREE.Matrix4().makeTranslation(0, 0, 3);
    let translationInvert = translationMatrix.clone().invert();

    allModels.forEach((model) => {
      if (model.isUpper) {
        if (isDoubleMode) {
          model.mesh.applyMatrix4(scaleMatrix);
          model.mesh.applyMatrix4(translationMatrix);
        } else {
          model.mesh.applyMatrix4(translationInvert);
          model.mesh.applyMatrix4(scaleInvert);
        }
      }
    });

    if (isDoubleMode) {
      let point = defaultIoBox.center;
      ioViewer.camera.position.x = point.x;
      ioViewer.camera.position.y = -defaultIoZoom;
      ioViewer.camera.position.z = point.z;

      ioViewer.camera.up.y = 0;
      ioViewer.camera.up.x = 0;
      ioViewer.camera.up.z = 1;
    }
  }

  switchTeethNumbers(showNumbers);
}

function resetIoCamera() {
  // let point = { x: 0, y: 0, z: 0 };
  let point = defaultIoBox.center;
  // console.log("resetIoCamera", point);
  ioViewer.camera.lookAt(point.x, point.y, point.z);
  ioViewer.controls.target = new THREE.Vector3(point.x, point.y, point.z);

  ioViewer.camera.up.y = 1;
  ioViewer.camera.up.x = 0;
  ioViewer.camera.up.z = 0;
  ioViewer.camera.position.x = 0; //point.x;
  ioViewer.camera.position.y = 0; //point.y;
  ioViewer.camera.position.z = defaultIoZoom;
}

function rotateIntraoralToView(viewNumber, clearFocus) {
  wheelPoint = null;
  // console.log("rotateIntraoralToView", viewNumber, ioViewer.camera);
  let focusedItem = allItems.find((t) => t.isFocused);
  if (focusedItem && clearFocus) {
    focusedItem.isFocused = false;
    switchItemFocus(focusedItem);
  }

  resetIoCamera();
  let viewQuarterinion1 = null;
  let viewQuarterinion2 = null;
  switch (viewNumber) {
    case 1:
      break;
    case 2:
      viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
        new THREE.Vector3(0, 0, 1), // z-axis
        degreesToRadians(180)
      );
      break;
    case 3:
      if (isUpperMode) {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(-90)
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          degreesToRadians(180)
        );
        // viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
        //   new THREE.Vector3(0, 1, 0),
        //   degreesToRadians(180)
        // );
        // viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
        //   new THREE.Vector3(1, 0, 0),
        //   degreesToRadians(-90)
        // );
      } else {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(90)
        );
      }
      break;
    case 4:
      if (isUpperMode) {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(-90)
        );
      } else {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(-90)
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 1, 0),
          degreesToRadians(180)
        );
      }

      break;
    case 5:
      if (isUpperMode) {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(-90)
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          degreesToRadians(-90)
        );
      } else {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(90)
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          degreesToRadians(-90)
        );
      }
      break;
    case 6:
      if (isUpperMode) {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(-90)
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          degreesToRadians(90)
        );
      } else {
        viewQuarterinion1 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(1, 0, 0),
          degreesToRadians(90)
        );
        viewQuarterinion2 = new THREE.Quaternion().setFromAxisAngle(
          new THREE.Vector3(0, 0, 1),
          degreesToRadians(90)
        );
      }
      break;
  }

  if (viewQuarterinion1) {
    ioViewer.camera.up.applyQuaternion(viewQuarterinion1);
    ioViewer.camera.position.applyQuaternion(viewQuarterinion1);
  }

  if (viewQuarterinion2) {
    ioViewer.camera.up.applyQuaternion(viewQuarterinion2);
    ioViewer.camera.position.applyQuaternion(viewQuarterinion2);
  }

  ioViewer.camera.updateProjectionMatrix();
  ioViewer.controls.update();
}

function setRotateCenter() {
  console.log("setRotateCenter");

  let focusedItem = allItems.find((t) => t.isFocused);
  if (!focusedItem && selectedTooth) focusedItem = selectedTooth.listItem;
  if (focusedItem) {
    let model = getModelByItem(focusedItem);
    if (model && model.mesh) {
      let box = model.defaultBox;
      if (!box) {
        model.mesh.geometry.computeBoundingBox();
        box = model.mesh.geometry.boundingBox;
        box = offsetBox(box);
      }
      let point = box.center;
      ioViewer.controls.target = new THREE.Vector3(point.x, point.y, point.z);
    }
  }
  // else if (wheelPoint) {
  //   ioViewer.controls.target.copy(wheelPoint);
  // }
  else {
    let point = defaultIoBox.center;
    ioViewer.controls.target = new THREE.Vector3(point.x, point.y, point.z);
  }
}

function changeTeethColor(teethColor) {
  console.log("changeTeethColor", teethColor);
  selectedTeethColor = teethColor;
  teeth.forEach((tooth) => {
    if (teethColor == randomColorValue) {
      let color = getColorFromToothNumber(tooth.number);
      ioViewer.set_color(tooth.id, color);
    } else if (teethColor == monochromeColorValue) {
      ioViewer.set_color(tooth.id, greyColorValue);
    } else {
      ioViewer.set_color(tooth.id, teethColor);
    }

    tooth.mesh.visible =
      !tooth.isDeleted &&
      !tooth.listItem.isHidden &&
      tooth.isVisible &&
      (tooth.isUpper === isUpperMode || isDoubleMode);
  });

  let gingivas = allModels.filter((model) => model.isGingiva);
  gingivas.forEach((model) => {
    if (teethColor == monochromeColorValue) {
      ioViewer.set_color(model.id, greyColorValue);
    } else {
      ioViewer.set_color(model.id, gingivaColor);
    }

    model.mesh.visible =
      !model.listItem.isHidden &&
      model.isVisible &&
      (model.isUpper === isUpperMode || isDoubleMode);
  });
}

function switchTeethNumbers(showTeethNumbers) {
  showNumbers = showTeethNumbers;
  // console.log("switchTeethNumbers", showNumbers);
  if (showTeethNumbers) {
    // rotateIntraoralToView(3);
    // if (isUpperMode) rotateIntraoralToView(2);
    // else rotateIntraoralToView(1);
    showLoader(true);

    removeTeethNumbers();
    drawTeethNumbers(ioViewer, allModels, isUpperMode);
    hideLoader();
  } else {
    removeTeethNumbers();
  }
}

function switchEditMode(isEdit) {
  isEditMode = viewMode.isEdit = isEdit;
  if (!isEditMode) {
    document.getElementById("selectedToothHint").innerHTML = "";
    intraoralMenu.style.display = "none";
    dragPoint = null;
    intersectedTooth = null;
    ioViewer.controls.enabled = true;
    teeth.forEach((model) => {
      if (model.pointsGroup) {
        model.pointsGroup.visible = false;
      }
    });
  } else {
    vueApp.leaveModes();
    loadEditData();
    teeth.forEach((model) => {
      if (model.pointsGroup && model == selectedTooth) {
        model.pointsGroup.visible = !model.listItem.isFocused;
      }
    });

    let anyFocused = isAnyModelFocused();
    gingivae.forEach((xmodel) => {
      if (xmodel) {
        xmodel.mainMesh.visible = false;
        xmodel.filledMesh.visible = true;
        xmodel.mesh = xmodel.filledMesh;
        xmodel.isVisible = xmodel.listItem.isVisible = true;
        xmodel.mesh.visible =
          xmodel.isUpper == isUpperMode &&
          (!anyFocused || xmodel.listItem.isFocused);
      }
    });
  }
}

function enterIntraoralOrtho() {
  switchEditMode(false);
  let anyFocused = isAnyModelFocused();
  gingivae.forEach((xmodel) => {
    if (xmodel) {
      xmodel.mainMesh.visible = true;
      xmodel.filledMesh.visible = false;
      xmodel.mesh = xmodel.mainMesh;
      xmodel.isVisible = xmodel.listItem.isVisible = true;
      xmodel.mesh.visible =
        xmodel.isUpper == isUpperMode &&
        (!anyFocused || xmodel.listItem.isFocused);
    }
  });
}

function showLoader(isGray) {
  $id("loader").style.display = "block";
  if (isGray) {
    $id("loader").style.background = "gray";
    $id("loader").style.opacity = "0.7";
  }
  $("#loaderText").html(`Loading data... Please wait`);
  $("#loaderText2").html(``);
  $("#loaderText3").html(``);
}

function hideLoader() {
  console.log("hideLoader");
  $id("loader").style.display = "none";
}

function switchFilledTeethView() {
  if (viewMode.isFilledTeethView) {
    if (!filledTeethLoaded) {
      viewMode.isFilledTeethView = false;
      // oldLoadFilledTeeth();
      if (viewMode.isPostprocessing) {
        swal({
          text: "Please wait for root stubs process to complete.",
        });
      } else {
        swal({
          text: "Root stubs not found. Would you like to generate them?",
          buttons: {
            yes: { text: "Yes", value: true },
            no: { text: "No", value: false },
          },
        }).then(async (postprocess) => {
          if (postprocess) {
            console.log(`postprocess patient ${vueApp.patientId}`);
            viewMode.isPostprocessing = true;
            sendStartPostprocessingRequest();
          }
        });
      }
    } else {
      showFilledTeeth();
    }
  } else {
    hideFilledTeeth();
    viewMode.isEdit = true;
    switchEditMode(viewMode.isEdit);
  }
}

function showFilledTeeth() {
  updateSelectedTooth(null);
  teeth.forEach((model) => {
    if (model.filledMesh) {
      model.mesh = model.filledMesh;
      model.mainMesh.visible = false;
      model.filledMesh.visible =
        model.isVisible &&
        !model.listItem.isHidden &&
        model.isUpper == isUpperMode;
    }
  });

  if (
    (upperGingiva && upperGingiva.listItem.isFocused) ||
    (lowerGingiva && lowerGingiva.listItem.isFocused)
  ) {
    switchItemFocus(null);
  }
  gingivae.forEach((xmodel) => {
    if (xmodel) {
      xmodel.mainMesh.visible = false;
      xmodel.filledMesh.visible = false;
      xmodel.mesh = xmodel.filledMesh;
      xmodel.mesh.visible =
        xmodel.isVisible =
        xmodel.listItem.isVisible =
          false;
    }
  });
}

function hideFilledTeeth() {
  let anyFocused = isAnyModelFocused();

  teeth.forEach((model) => {
    if (model.filledMesh) {
      model.mesh = model.mainMesh;
      model.filledMesh.visible = false;
      model.mainMesh.visible =
        model.isVisible &&
        !model.listItem.isHidden &&
        model.isUpper == isUpperMode &&
        (!anyFocused || model.listItem.isFocused);
    }
  });
  gingivae.forEach((xmodel) => {
    if (xmodel) {
      xmodel.mainMesh.visible = false;
      xmodel.filledMesh.visible = true;
      xmodel.mesh = xmodel.filledMesh;
      xmodel.isVisible = xmodel.listItem.isVisible = true;
      xmodel.mesh.visible =
        xmodel.isUpper == isUpperMode &&
        (!anyFocused || xmodel.listItem.isFocused);
    }
  });
}
//#endregion

//#region Save/Reset

function saveIntraoralData(updateExportedData) {
  let upperPointsData = {};
  let lowerPointsData = {};
  let upperAnchorsData = {};
  let lowerAnchorsData = {};
  let settingsData = { needPostprocess: !!updateExportedData, teeth: {} };
  var zip = new JSZip();
  teeth.forEach((tooth) => {
    // let isChanged = tooth.savedGeometry != tooth.mesh.geometry;
    settingsData.teeth[tooth.name] = {
      color: tooth.color,
      baseNumber: tooth.baseNumber,
      number: tooth.number,
      glbNumber: tooth.glbNumber,
      isSaved: true,
      isUpper: tooth.isUpper,
      isAdded: tooth.isAdded,
      isDeleted: tooth.isDeleted,
      isChanged: tooth.isChanged,
      crossedTeeth: tooth.crossedTeeth,
    };
    if (tooth.isChanged) {
      console.log("changed tooth: ", tooth.name);
    }
    tooth.baseNumber = tooth.number;
    tooth.recentlyAdded = tooth.recentlyDeleted = false;
    tooth.isChanged = false;
    if (tooth.isDeleted) return;

    let stlBlob = exportMeshToStlBlob(tooth.mesh);
    tooth.savedGeometry = tooth.mesh.geometry;
    tooth.savedPoints = getSavedBorderPointsArr(tooth);
    // tooth.savedAnchorPoints = getSavedAnchorPointsArr(tooth);
    tooth.savedAnchorGroup = getSavedAnchorGroup(tooth);

    zip.file(EXTR_TAG + tooth.name + ".stl", stlBlob);

    if (tooth.isUpper) {
      upperPointsData[tooth.name] = tooth.savedPoints;
      if (tooth.savedAnchorGroup && tooth.savedAnchorGroup.length) {
        for (let i = 0; i < tooth.savedAnchorGroup.length; i++) {
          const borderGroup = tooth.savedAnchorGroup[i];
          let tname = i > 0 ? `${tooth.name}-${i}` : tooth.name;
          upperAnchorsData[tname] = borderGroup;
        }
      }
    } else {
      lowerPointsData[tooth.name] = tooth.savedPoints;
      if (tooth.savedAnchorGroup && tooth.savedAnchorGroup.length) {
        for (let i = 0; i < tooth.savedAnchorGroup.length; i++) {
          const borderGroup = tooth.savedAnchorGroup[i];
          let tname = i > 0 ? `${tooth.name}-${i}` : tooth.name;
          lowerAnchorsData[tname] = borderGroup;
        }
      }
    }
  });
  let settingsString = JSON.stringify(settingsData);
  console.log(settingsString);
  zip.file("intraoral_settings.json", settingsString);

  if (upperMesh) {
    zip.file("saved_upper_border_points.json", JSON.stringify(upperPointsData));
    zip.file("saved_upper_extr_anchors.json", JSON.stringify(upperAnchorsData));
  }

  if (lowerMesh) {
    zip.file("saved_lower_border_points.json", JSON.stringify(lowerPointsData));
    zip.file("saved_lower_extr_anchors.json", JSON.stringify(lowerAnchorsData));
  }

  console.log("saveIntraoralData", zip);

  zip.generateAsync({ type: "blob" }).then(async function (blob_data) {
    await sendSaveIntraoralDataRequest(
      blob_data,
      upperMesh && lowerMesh,
      filledTeethLoaded // TODO: callback
    );
    viewMode.isSaving = false;
    postprocessCheck();
  });
}

function resetIoView() {
  viewMode.isFilledTeethView = false;
  switchFilledTeethView();
  updateSelectedTooth(null);
  intraoralMenu.style.display = "none";
  dragPoint = null;
  intersectedTooth = null;
  ioViewer.controls.enabled = true;
}

function globalResetIntraoral() {
  resetIoView();
  clearTempMeshes(ioViewer.scene);

  teeth.forEach((model) => {
    let item = model.listItem;
    if (model.glbNumber && model.number != model.glbNumber) {
      let glbName = `tooth_${model.glbNumber}`;
      model.name = item.name = item.displayName = glbName;
      model.number = item.number = model.glbNumber;
      var mesh = model.mainMesh;
      if (mesh && mesh.numberGroup) {
        ioViewer.scene.remove(mesh.numberGroup);
        mesh.numberGroup = null;
        if (showNumbers) showToothNumber(model);
      }
    }

    let vmesh = model.mesh;
    if (model.isDeleted && !model.isAdded) {
      model.isDeleted = item.isDeleted = false;
      model.recentlyAdded = true;
      vmesh.visible = true;
    }

    if (model.isAdded) {
      model.isDeleted = model.recentlyDeleted = item.isDeleted = true;
      vmesh.visible = false;

      var mesh = model.mainMesh;
      if (mesh && mesh.numberGroup) {
        ioViewer.scene.remove(mesh.numberGroup);
        mesh.numberGroup = null;
      }
    }

    if (vmesh.visible && model.baseGeometry) {
      let cloneGeometry = model.baseGeometry.clone();
      cloneGeometry.computeBoundingBox();

      let box = cloneGeometry.boundingBox;
      box = offsetBox(box, 0, 0, globalDz);
      // vmesh.position.set(box.center.x, box.center.y, box.center.z);

      let geometry = prepareGeometry(cloneGeometry, model.isGingiva);
      vmesh.geometry.dispose();
      vmesh.geometry = geometry;
    }

    if (model.pointsGroup) {
      ioViewer.scene.remove(model.pointsGroup);
      model.pointsGroup = null;
    }

    if (model.basePoints) {
      model.points = model.basePoints;
      model.anchorPoints = model.baseAnchorPoints;
      model.anchorGroup = model.baseAnchorGroup;
      if (model == selectedTooth && !model.isDeleted) {
        document.getElementById("selectedToothHint").innerHTML = model.name;
        createPointsGroup(model);
        model.pointsGroup.visible = !item.isFocused;
      }
    }
  });

  clearIoChanges();
  // selectedTooth = null;
}

function resetIntraoral() {
  resetIoView();
  clearTempMeshes(ioViewer.scene);

  // resetIoCamera();
  teeth.forEach((model) => {
    let item = model.listItem;
    if (model.baseNumber && model.number != model.baseNumber) {
      let baseName = `tooth_${model.baseNumber}`;
      model.name = item.name = item.displayName = baseName;
      model.number = item.number = model.baseNumber;
      var mesh = model.mainMesh;
      if (mesh && mesh.numberGroup) {
        ioViewer.scene.remove(mesh.numberGroup);
        mesh.numberGroup = null;
        if (showNumbers) showToothNumber(model);
      }
    }

    let vmesh = model.mesh;
    if (model.recentlyDeleted) {
      model.isDeleted = model.recentlyDeleted = item.isDeleted = false;
      vmesh.visible = true;
    }

    if (model.recentlyAdded) {
      model.isDeleted = item.isDeleted = true;
      vmesh.visible = false;
      var mesh = model.mainMesh;
      if (mesh && mesh.numberGroup) {
        ioViewer.scene.remove(mesh.numberGroup);
        mesh.numberGroup = null;
      }
    }

    if (model.savedGeometry && vmesh.visible) {
      let cloneGeometry = model.savedGeometry; //.clone();
      vmesh.geometry.dispose();
      vmesh.geometry = cloneGeometry;
    }

    if (model.pointsGroup) {
      ioViewer.scene.remove(model.pointsGroup);
      model.pointsGroup = null;
    }

    let sPoints = model.savedPoints ? model.savedPoints : model.basePoints;
    let aPoints = model.savedAnchorPoints
      ? model.savedAnchorPoints
      : model.baseAnchorPoints;
    let anchorGroup = model.savedAnchorGroup
      ? model.savedAnchorGroup
      : model.anchorGroup;
    if (sPoints) {
      model.points = sPoints;
      model.anchorPoints = aPoints;
      model.anchorGroup = anchorGroup;
      if (model == selectedTooth && !model.isDeleted) {
        createPointsGroup(model);
        model.pointsGroup.visible = !item.isFocused;
        document.getElementById("selectedToothHint").innerHTML = model.name;
      }
    }
  });
  clearIoChanges();
  // selectedTooth = null;
}

//#endregion

//#region Undo/Redo

function addIoChange(change) {
  if (currentIoChange) {
    let length = ioChanges.length;
    let index = ioChanges.indexOf(currentIoChange) + 1;
    let deletedChanges = ioChanges.splice(index, length - index);
    deletedChanges.forEach((c) => {
      c.oldGeometry.dispose();
      c.newGeometry.dispose();
    });
  } else {
    clearIoChanges();
  }
  ioChanges.push(change);
  currentIoChange = change;
}

function applyIoChange(change, applyNew) {
  if (!change) return;

  let model = change.model;
  if (model && !model.isDeleted) {
    let vmesh = model.mesh;
    let cloneGeometry = applyNew ? change.newGeometry : change.oldGeometry;
    vmesh.geometry = cloneGeometry;

    if (model.pointsGroup) {
      ioViewer.scene.remove(model.pointsGroup);
      model.pointsGroup = null;
    }

    model.points = applyNew ? change.newPoints : change.oldPoints;
    model.anchorPoints = applyNew
      ? change.newAnchorPoints
      : change.oldAnchorPoints;
    model.anchorGroup = applyNew
      ? change.newAnchorGroup
      : change.oldAnchorGroup;
    if (
      selectedTooth != null &&
      selectedTooth != model &&
      selectedTooth.pointsGroup
    ) {
      selectedTooth.pointsGroup.visible = false;
    }
    selectedTooth = model;
    createPointsGroup(model);
    model.pointsGroup.visible = !model.listItem.isFocused;
    document.getElementById("selectedToothHint").innerHTML = model.name;
  }
}

function intraoralUndo() {
  if (currentIoChange) {
    let index = ioChanges.indexOf(currentIoChange);
    // console.log("intraoralUndo", index, currentIoChange);
    applyIoChange(currentIoChange, false);
    currentIoChange = ioChanges[index - 1];
  }
}

function intraoralRedo() {
  let index = ioChanges.indexOf(currentIoChange) + 1;
  let nextIoChange = ioChanges[index];
  // console.log("intraoralRedo", index, nextIoChange);
  if (nextIoChange) {
    applyIoChange(nextIoChange, true);
    currentIoChange = nextIoChange;
  }
}

function clearIoChanges() {
  ioChanges.forEach((change) => {
    change.oldGeometry.dispose();
    change.newGeometry.dispose();
  });

  ioChanges = [];
}

//#endregion

//#region Postprocess

const postprocessInterval = 60000;
let postprocessVar = null;

function startPostprocessWatch() {
  postprocessCheck();
  postprocessVar = setInterval(postprocessCheck, postprocessInterval);
}

function stopPostprocessWatch() {
  clearInterval(postprocessVar);
}

async function postprocessCheck() {
  let isPostprocessing = await checkPatientInPostprocess(vueApp.patientId);
  // console.log("postprocessCheck", isPostprocessing);
  viewMode.isPostprocessing = isPostprocessing;
}

//#endregion

//#region Export

export {
  EXTR_TAG,
  CUT_TAG,
  SMOOTHED_TAG,
  TOOTH_TAG,
  STL_TAG,
  ioViewer,
  ioPlane,
  ioRaycaster,
  allModels,
  gingivae,
  getIoMouse3dDirection,
  checkIsEditMode,
  switchFilledTeethView,
  fillIntraoralList,
  addClick,
  changeItemVisibility,
  deleteItem,
  editItemNumber,
  highlightItem,
  unhighlightItem,
  switchItemFocus,
  randomColorValue,
  monochromeColorValue,
  greyColorValue,
  changeTeethColor,
  saveIntraoralData,
  resetIntraoral,
  globalResetIntraoral,
  switchEditMode,
  enterIntraoralOrtho,
  showIoViewer,
  setAllModels,
  initStlViewer,
  applyBorderPoints,
  applyAnchorPoints,
  switchViewModes,
  cancelAdd,
  rotateIntraoralToView,
  setRotateCenter,
  smoothSelectedToothBorder,
  repairIntraoralColouring,
  intraoralUndo,
  intraoralRedo,
  updateOpacity,
  getModelByName,
  startAddingTooth,
  switchTeethNumbers,
  changeToothNumber,
  startPostprocessWatch,
  stopPostprocessWatch,
  postprocessCheck,
  viewMode,
};
//#endregion
